/**
 * JAVASCRIPT DEVELOPER DOCUMENTATION
 *
 * Symmetry is a powerful and customizable theme designed for large-scale e-commerce stores. Built
 * using Web Components, it offers a highly modular architecture that makes customization and
 * maintenance easier than ever. In addition, Symmetry is optimized for speed, ensuring that your
 * store runs as fast as possible to provide your customers with a seamless shopping experience.
 *
 * If you would like to add your own JS to Symmetry, we recommend using this file and referencing
 * it using Theme Settings > Advanced > Custom HTML.
 *
 * As a brief overview, Symmetry:
 *  - Broadcasts many JS events.
 *  - Is built using Web Components.
 *  - Leverages 'code splitting' for some features.
 *  - Is completely custom built (no JS libraries)
 *  - Has a number of JS utilities.
 *
 *
 *
 * =================================================================================================
 * Custom JavaScript Events
 * =================================================================================================
 *
 * Symmetry broadcasts many custom events for ease of extensibility, detailed in this section.
 *
 * When in the Theme Editor, the details of each custom event will be logged out in the Dev Tools
 * console everytime it is triggered.
 *
 * Events are named in the following convention: ["on/dispatch"]:[subject]:[action] (where
 * 'dispatch' will trigger an event to occur, and 'on' indicates an event has occurred).
 *
 * All 'Return data' detailed in this section is returned within the 'event.detail' object.
 *
 * The available events are:
 *  1.  on:variant:change
 *  2.  on:cart:add
 *  3.  on:cart:error
*   4.  on:cart:after-merge
 *  5.  on:cart-drawer:before-open
 *  6.  on:cart-drawer:after-open
 *  7.  on:cart-drawer:after-close
 *  8.  on:quickbuy:before-open
 *  9.  on:quickbuy:after-open
 *  10. on:quickbuy:after-close
 *  11. dispatch:cart-drawer:open
 *  12. dispatch:cart-drawer:refresh
 *  13. dispatch:cart-drawer:close
 *  14. on:debounced-resize
 *  15. on:breakpoint-change
 *
 * -------------------------------------------------------------------------------------------------
 * 1) on:variant:change
 * -------------------------------------------------------------------------------------------------
 * Fires whenever a variant is selected (e.g. Product page, Quickbuy, Featured Product etc).
 *
 * How to listen:
 * document.addEventListener('on:variant:change', (event) => {
 *  // your code here
 * });
 *
 * Returned data:
 *  - form: the product form content
 *  - variant: the selected variant object
 *  - allVariants: an array of all variants
 *  - selectedOptions: an array of currently selected options (e.g. ['Blue', 'Large'])
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 2) on:cart:add
 * -------------------------------------------------------------------------------------------------
 * Fires when a variant has been added to the cart, where it didn't exist in the cart before. This
 * event does not fire when the added variant was already in the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:add', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - variantId: id of the variant that was just added to the cart
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 3) on:cart:error
 * -------------------------------------------------------------------------------------------------
 * Fires when an action related to the cart has failed, for example adding too much quantity of an
 * item to the cart.
 *
 * How to listen:
 * document.addEventListener('on:cart:error', (event) => {
 *   // your code here
 * });
 *
 * Returned data:
 *   - error: the error message
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 4) on:cart:after-merge
 * -------------------------------------------------------------------------------------------------
 * Fires after a list of cart items has finished being dynamically updated after a cart change.
 *
 * How to listen:
 * document.addEventListener('on:cart:after-merge', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 5) on:cart-drawer:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the cart drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 6) on:cart-drawer:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 7) on:cart-drawer:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the cart drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:cart-drawer:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 8) on:quickbuy:before-open
 * -------------------------------------------------------------------------------------------------
 * Fires before the quick buy drawer opens.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:before-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 9) on:quickbuy:after-open
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished opening.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-open', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 10) on:quickbuy:after-close
 * -------------------------------------------------------------------------------------------------
 * Fires after the quick buy drawer has finished closing.
 *
 * How to listen:
 * document.addEventListener('on:quickbuy:after-close', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 11) dispatch:cart-drawer:open
 * -------------------------------------------------------------------------------------------------
 * Opens the cart drawer (if enabled in the Theme Settings).
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
 *
 * You can optionally pass in a 'detail' object with a property of 'opener', which specifies the DOM
 * element that should be focussed on when the drawer is closed.
 *
 * Example:
 * document.getElementById('header-search').addEventListener('keydown', (evt) => {
 *   if (evt.keyCode === 67) {
 *     evt.preventDefault();
 *     document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open', {
 *       detail: {
 *         opener: evt.target
 *       }
 *     }));
 *   }
 * });
 *
 * In this example, we attach a keydown listener to the search input in the header. If the user
 * presses the 'c' key, it prevents the default behavior (which would be to type the letter 'c' in
 * the input) and dispatches the 'dispatch:cart-drawer:open' event with a 'detail' object that
 * specifies the search input as the opener. When the cart drawer is closed, focus is returned to
 * the search input.
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 12) dispatch:cart-drawer:refresh
 * -------------------------------------------------------------------------------------------------
 * Refreshes the contents of the cart drawer.
 *
 * This event is useful when you are adding variants to the cart and would like to instruct the
 * theme to re-render the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:refresh', {
 *   bubbles: true
 * }));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 13) dispatch:cart-drawer:close
 * -------------------------------------------------------------------------------------------------
 * Closes the cart drawer.
 *
 * How to trigger:
 * document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:close'));
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 14) on:debounced-resize
 * -------------------------------------------------------------------------------------------------
 * Fires when the viewport finishes resizing (debounced to 300ms by default).
 *
 * How to listen:
 * window.addEventListener('on:debounced-resize', (event) => {
 *   // your code here
 * });
 *
 *
 * -------------------------------------------------------------------------------------------------
 * 15) on:breakpoint-change
 * -------------------------------------------------------------------------------------------------
 * Fires when the breakpoint of the viewport changes.
 *
 * Example:
 *
 *
 *
 * =================================================================================================
 * Web Components
 * =================================================================================================
 *
 * Symmetry utilizes Web Components to the fullest.
 *
 * Web Components are a set of standardized APIs that allow developers to create custom, reusable
 * HTML elements that can be used across different web pages and applications.
 * Web Components consist of three main technologies: Custom Elements, Shadow DOM and HTML
 * Templates.
 *
 * See Mozilla for more: https://developer.mozilla.org/en-US/docs/Web/Web_Components
 *
 *
 *
 =================================================================================================
 * Third-Party JavaScript Dependencies
 * =================================================================================================
 *
 * Symmetry has no third-party JavaScript dependencies.
 *
 *
 * =================================================================================================
 *
 * Have fun! - The Clean Canvas Development Team.
 */
// Select the sticky filter element


/**
 * SR Custom Components
 */

/* SR Tab Content */
/* ============================================================
   SR TAB CONTENTS
============================================================ */

class SRTabContents extends HTMLElement {
  constructor() {
    super();

    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
  }

  connectedCallback() {
    if (this.dataset.tabsInitialized === 'true') return;

    this.dataset.tabsInitialized = 'true';

    this.addEventListener('click', this.handleClick);
    this.addEventListener('keydown', this.handleKeydown);

    this.setupTabs();
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.handleClick);
    this.removeEventListener('keydown', this.handleKeydown);
  }

  setupTabs() {
    const tabs = this.querySelectorAll('sr-tab');

    tabs.forEach((tab) => {
      tab.setAttribute('role', 'tab');
      tab.setAttribute('tabindex', '0');

      tab.setAttribute(
        'aria-selected',
        tab.classList.contains('active') ? 'true' : 'false'
      );
    });
  }

  handleClick(event) {
    const tab = event.target.closest('sr-tab');

    if (!tab || !this.contains(tab)) return;

    event.preventDefault();

    this.activateTab(tab);
  }

  handleKeydown(event) {
    if (event.key !== 'Enter' && event.key !== ' ') return;

    const tab = event.target.closest('sr-tab');

    if (!tab || !this.contains(tab)) return;

    event.preventDefault();

    this.activateTab(tab);
  }

  activateTab(tab) {
    const targetClass = tab.dataset.tab;

    if (!targetClass) {
      console.warn('[SR Tabs] Missing data-tab:', tab);
      return;
    }

    const tabs = this.querySelectorAll('sr-tab');
    const contents = this.querySelectorAll('sr-tab-content');

    /*
     * Reset tabs
     */
    tabs.forEach((item) => {
      item.classList.remove('active');
      item.setAttribute('aria-selected', 'false');
    });

    /*
     * Reset content
     */
    contents.forEach((content) => {
      content.classList.remove('active');
    });

    /*
     * Find content using:
     *
     * data-tab="sr-tab2"
     * ->
     * <sr-tab-content class="sr-tab2">
     */
    const targetContent = this.querySelector(
      `sr-tab-content.${CSS.escape(targetClass)}`
    );

    if (!targetContent) {
      console.warn(
        '[SR Tabs] Content not found:',
        targetClass
      );

      return;
    }

    tab.classList.add('active');
    tab.setAttribute('aria-selected', 'true');

    targetContent.classList.add('active');
  }
}

/* Prevent custom element duplicate registration errors */
if (!customElements.get('sr-tab-contents')) {
  customElements.define('sr-tab-contents', SRTabContents);
}
/* SR Tab Content end */

/* SR Green Wolf Product Card */
class SRGWProductCard extends HTMLElement {
    constructor() {
        super();

        this.initialized = false;
    }

    connectedCallback() {
        // Wait until Shopify/browser has parsed
        // the contents inside the custom element.
        requestAnimationFrame(() => {
            this.init();
        });
    }

    init() {
        if (this.initialized) return;

        const colorDots = this.querySelectorAll(
            'input[type="radio"]'
        );

        const button = this.querySelector(
            'a.btn-gw-product-card'
        );

  

        if (!colorDots.length) {
            setTimeout(() => {
                this.init();
            }, 100);

            return;
        }

        this.initialized = true;

        const selectedColor =
            this.querySelector('input[type="radio"]:checked') ||
            colorDots[0];

        if (selectedColor) {
            this.updateCard(selectedColor);
        }

        colorDots.forEach(colorDot => {
            colorDot.addEventListener('change', event => {
                this.updateCard(event.currentTarget);
            });
        });
    }

    updateCard(colorDot) {
        const productUrl = colorDot.dataset.prodUrl;

        if (!productUrl) {
            console.warn(
                'Missing data-prod-url:',
                colorDot
            );

            return;
        }

        const imgMobile = this.querySelector(
            '.product-image picture img.mobile-only'
        );

        const imgDesktop = this.querySelector(
            '.product-image picture img.desktop-only'
        );

        if (imgMobile) {
            if (colorDot.dataset.imgMob) {
                imgMobile.src =
                    colorDot.dataset.imgMob;
            }

            if (colorDot.dataset.imgSrcsetMob) {
                imgMobile.srcset =
                    colorDot.dataset.imgSrcsetMob;
            }
        }

        if (imgDesktop) {
            if (colorDot.dataset.imgDesk) {
                imgDesktop.src =
                    colorDot.dataset.imgDesk;
            }

            if (colorDot.dataset.imgSrcsetDesk) {
                imgDesktop.srcset =
                    colorDot.dataset.imgSrcsetDesk;
            }
        }

        const button = this.querySelector(
            'a.btn-gw-product-card'
        );

        if (button) {
            button.href = productUrl;
        }

        const callbackClass =
            colorDot.dataset.callbackClass;

        if (callbackClass) {
            const buttonTile = document.querySelector(
                `a.${CSS.escape(callbackClass)}`
            );

            if (buttonTile) {
                buttonTile.href = productUrl;
            }
        }

    }
}

if (!customElements.get('sr-gw-product-card')) {
    customElements.define(
        'sr-gw-product-card',
        SRGWProductCard
    );
}

class SRImageComparison extends HTMLElement {
  constructor(parameters) {
      super();
  }


  static applyClip(root, percentage) {
      const p = Math.max(0, Math.min(100, percentage));
      const layerAfter = root.querySelector('.comparison-slider__layer--after');
      const imgAfter = root.querySelector('img.image-after');
      const target = layerAfter || root.querySelector('.image-after');
      if (!target) return;
      target.style.clipPath = `inset(0 ${100 - p}% 0 0)`;
      if (layerAfter && imgAfter) {
          imgAfter.style.setProperty('clip-path', 'none', 'important');
      }
  }

  connectedCallback() {
      if (this.dataset.srCompareInit === '1') return;
      this.dataset.srCompareInit = '1';

      const slider = this.querySelector('.comparison-slider');
      const handle = this.querySelector('.slider-handle');
      if (!slider || !handle) return;

      const color_dot = Array.from(this.querySelectorAll('input[type="radio"]'));
      color_dot.forEach(cd => {
          cd.addEventListener('change', (event) => {
              this.handleColorDotChange(event);
          });
      });

      let isDragging = false;

      const applyFromClientX = (clientX) => {
          const rect = slider.getBoundingClientRect();
          if (rect.width <= 0) return;
          let x = clientX - rect.left;
          if (x < 0) x = 0;
          if (x > rect.width) x = rect.width;
          const percentage = (x / rect.width) * 100;
          handle.style.left = `${percentage}%`;
          SRImageComparison.applyClip(this, percentage);
      };

      SRImageComparison.applyClip(this, 50);

      let dragPointerId = null;
      const onPointerDown = (e) => {
        if (e.pointerType === 'mouse' && e.button !== 0) return;
        dragPointerId = e.pointerId;
        try {
          slider.setPointerCapture(e.pointerId);
        } catch (_) {}
        applyFromClientX(e.clientX);
      };
      const onPointerMove = (e) => {
        if (dragPointerId == null || e.pointerId !== dragPointerId) return;
        e.preventDefault();
        applyFromClientX(e.clientX);
      };
      const endPointerDrag = (e) => {
        if (dragPointerId == null || (e && e.pointerId !== dragPointerId)) return;
        dragPointerId = null;
        try {
          if (slider.hasPointerCapture(e.pointerId)) slider.releasePointerCapture(e.pointerId);
        } catch (_) {}
      };
      slider.addEventListener('pointerdown', onPointerDown);
      slider.addEventListener('pointermove', onPointerMove);
      slider.addEventListener('pointerup', endPointerDrag);
      slider.addEventListener('pointercancel', endPointerDrag);

      handle.addEventListener('mousedown', () => {
        if (dragPointerId != null) return;
        isDragging = true;
      });
      this.addEventListener('mouseup', () => {
        isDragging = false;
      });
      this.addEventListener('mousemove', (e) => {
        if (!isDragging || dragPointerId != null) return;
        applyFromClientX(e.clientX);
      });
      handle.addEventListener('touchstart', () => {
        if (dragPointerId != null) return;
        isDragging = true;
      });
      this.addEventListener('touchend', () => {
        isDragging = false;
      });
      this.addEventListener('touchmove', (e) => {
        if (!isDragging || dragPointerId != null) return;
        const touch = e.touches[0];
        if (!touch) return;
        applyFromClientX(touch.clientX);
      });
  }

  handleColorDotChange(event) {
      const target_color_dot = event.target;
      const label = document.querySelectorAll('.img-comp-after')[0];
      const comparison_slider = document.querySelectorAll('.comparison-slider')[0];
      if (!label || !comparison_slider) return;
      const before = comparison_slider.querySelector('.image-before');
      if (!before) return;
      label.innerHTML = target_color_dot.dataset.title;
      before.src = target_color_dot.dataset.imgAfter;
      before.setAttribute('srcset', target_color_dot.dataset.imgAfter);
  }
}
customElements.define('sr-image-comparison', SRImageComparison);
/* SR Image Comparison end */

/* SR Product Card with Color dots */
SRProductCardWithCD = class extends HTMLElement {
  constructor() {
      super();
  }

  connectedCallback() {
      this.addEventListener('change', this.handleColorDotChange.bind(this));
      window.addEventListener('DOMContentLoaded', () => {
          const selectedRadio = this.querySelector('input[type="radio"]:checked');
          if (selectedRadio) {
              this.handleColorDotChange({ target: selectedRadio });
          }
      });
  }

  handleColorDotChange(event) {
      if (event.target.type === "radio") {
          const sel_cd = event.target;
          const prod_info = this.querySelector('.sr-product-info');
          const image1 = prod_info.querySelector('img.default-image');
          const image2 = prod_info.querySelector('img.hover-image');
          const price = prod_info.querySelector('.product-price');
          const title = prod_info.querySelector('.collection-name');
          const a_link = prod_info.querySelectorAll('a');
          image1.src = sel_cd.dataset.img1;
          image1.setAttribute('srcset', sel_cd.dataset.img1Srcset);

          image2.src = sel_cd.dataset.img2;
          image2.setAttribute('srcset', sel_cd.dataset.img2Srcset);
          title.innerHTML = sel_cd.dataset.title;
          price.innerHTML = sel_cd.dataset.price;

          this.quick_buy_btn = this.querySelector('.sr-quick-buy-btn');

          a_link.forEach((link) => {
              link.setAttribute('href', sel_cd.dataset.prodUrl);
              if (this.quick_buy_btn) {
                  this.quick_buy_btn.setAttribute('data-id', sel_cd.value);
                  this.quick_buy_btn.setAttribute('data-handle', sel_cd.dataset.handle);
              }
          });
          if (sel_cd.dataset.showAllTags) {
              // code here!
          }
          setTimeout(() => {
              MarkdownPricePro.run();
          }, 400);
      }
  }
}
customElements.define('sr-product-card-cd', SRProductCardWithCD);
/* SR Product Card with Color dots end */

function checkHighDemand() {
  const cartHighDemand = document.querySelector('.cart-high-demand');
  const cartHasHighDemand = document.querySelectorAll('[data-high-demand="true"]');
  if (cartHighDemand) {
      if (cartHasHighDemand.length > 0) {
          cartHighDemand.classList.remove('hidden');
      } else {
          cartHighDemand.classList.add('hidden');
      }
  }
}
document.addEventListener('DOMContentLoaded', function () {

  // Update Kids Menu Labels Helper
  function updateKidsMenuLabels() {
    const isMobile = window.innerWidth < 768;

    document.querySelectorAll('.kids-menus a[data-handle="youth-sport"] span').forEach(span => {
      span.textContent = isMobile ? 'Youth' : 'Youth Sport';
    });

    document.querySelectorAll('.kids-menus a[data-handle="kids-sunglesses"] span').forEach(span => {
      span.textContent = isMobile ? 'Kids' : "Kids' Sunglasses";
    });
  }

  updateKidsMenuLabels();
  window.addEventListener('resize', updateKidsMenuLabels);

  //discountApres();
  if (!document.body.classList.contains('loop-returns-activated')) {
      checkHighDemand();
  }
    
  
  document.querySelector('.sr-btn-cart')?.classList.add('hidden');
  document.querySelector('.sr-btn-checkout')?.classList.remove('hidden'); 

  setTimeout(() => {
   

      if (document.body.classList.contains('loop-returns-activated')) {
          document.querySelectorAll('a.cart-item__remove[data-is-rx="true"]').forEach(el => {
              el.click();
          });
          
          if (document.querySelector('.sr-btn-cart'))
            document.querySelector('.sr-btn-cart').classList.remove('hidden');
        
          if (document.querySelector('.sr-btn-checkout'))
            document.querySelector('.sr-btn-checkout').classList.add('hidden');
          document.querySelectorAll('.cart-discount-list , .discount-at20').forEach(el => {
              el.style.display = 'none';
          });
          document.querySelectorAll('.cart-drawer , cart-form').forEach(drawer => {
              drawer.querySelectorAll('.markdown-price').forEach(el => {
                  el.style.display = 'none';
              });
              
              drawer.querySelectorAll('.original-price').forEach(el => {
                  el.classList.remove('price__was');
              });
          });
      } else {
          if (document.querySelector('.sr-btn-cart'))
            document.querySelector('.sr-btn-cart').classList.add('hidden');
        
          if (document.querySelector('.sr-btn-checkout'))
            document.querySelector('.sr-btn-checkout').classList.remove('hidden');          
        }

    if (document.body.classList.contains('loop-returns-activated')) {

        document.querySelectorAll('a.cart-item__remove[data-is-rx="true"]').forEach(el => {
            el.click();
        });
        

        
        document.querySelectorAll('.cart-discount-list , .discount-at20').forEach(el => {
            el.style.display = 'none';
        });

        document.querySelectorAll('.cart-drawer, cart-form').forEach(drawer => {
            drawer.querySelectorAll('.markdown-price').forEach(el => {
                el.style.display = 'none';
            });
            
            drawer.querySelectorAll('.original-price').forEach(el => {
                el.classList.remove('price__was');
            });
        });


          const ex_product_ids = [8174456766511, 8174457913391, 8174457487407]; 

          document.querySelectorAll('sr-product-card-new').forEach(card => {

            const productId = Number(card.dataset.productId);



            if (ex_product_ids.includes(productId)) {

              card.remove();

            }
            });   
    }
}, 700);

// Function to validate email format
function checkIsValidEmail(email) {
    var regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
}

  // Handle click on elements with class 'btn-check-vip-status'
  document.querySelectorAll('.btn-check-vip-status').forEach(function (element) {
      element.addEventListener('click', function (e) {
          e.preventDefault();

          const form = this.closest('form');
          const button = this;
          const emailInput = form.querySelector('.stausemail');

          button.classList.add('is-loading');
          button.classList.remove('is-success');
          button.setAttribute('disabled', 'disabled');

          if (emailInput && emailInput.value && checkIsValidEmail(emailInput.value)) {
              checkVIPStatusByEmailForPopUp(btoa(emailInput.value), function (response) {
                  button.classList.remove('is-loading');
                  button.removeAttribute('disabled');
                  button.classList.add('is-success');
              });
          } else {
              button.classList.remove('is-loading');
              button.removeAttribute('disabled');
          }
      });
  });
  checkMysteryPairProduct();
});

document.addEventListener('DOMContentLoaded', () => {
document.querySelectorAll('sr-product-card-new[data-has-url="true"]').forEach(card => {
  const url = card.dataset.url;
  const block = card.querySelector('.sr-custom-block-container');

  if (!url || !block) return;

  block.style.cursor = 'pointer';

  block.addEventListener('click', (e) => {
    e.preventDefault();
    window.location.href = url;
  });
});
});

document.addEventListener('on:cart:after-merge', function (e) {



  setTimeout(() => {
      if (document.body.classList.contains('loop-returns-activated')) {
          document.querySelector('.sr-btn-cart').classList.remove('hidden');
          document.querySelector('.sr-btn-checkout').classList.add('hidden');
          document.querySelectorAll('.cart-discount-list , .discount-at20').forEach(el => {
              el.style.display = 'none';
          });
          document.querySelectorAll('.cart-drawer , cart-form ').forEach(drawer => {
              drawer.querySelectorAll('.markdown-price').forEach(el => {
                  el.style.display = 'none';
              });
              
              drawer.querySelectorAll('.original-price').forEach(el => {
                  el.classList.remove('price__was');
              });
          });
      } else {
          if (document.querySelector('.sr-btn-cart'))
            document.querySelector('.sr-btn-cart').classList.add('hidden');
        
          if (document.querySelector('.sr-btn-checkout'))
            document.querySelector('.sr-btn-checkout').classList.remove('hidden');          
      }
  }, 500);
  
  setTimeout(() => {
      document.querySelectorAll('.cart-accordion').forEach((accordion) => {
          const toggleButton = accordion.querySelector('.accordion-angle');
          toggleButton.addEventListener('click', () => {
              accordion.classList.toggle('active');

              document.querySelectorAll('.cart-accordion').forEach((otherAccordion) => {
                  if (otherAccordion !== accordion) {
                      otherAccordion.classList.remove('active');
                  }
              });
          });
      });

      const cartItemJson = document.querySelector('script[data-json-id="cart-json"]');
      if (cartItemJson) {
          theme.globals.cartItems = JSON.parse(cartItemJson.innerHTML);
      }

  }, 100);

  setTimeout(() => {
      if (!document.body.classList.contains('loop-returns-activated')) {
          if(window.Shopify.vanity){
              window.Shopify.vanity.init();
          }
      }
      handleVisibleCartAtom();
  }, 700);

    setTimeout(() => {
        if (!document.body.classList.contains('loop-returns-activated')) {
            if(window.Shopify.vanity){
                window.Shopify.vanity.init();
            }
        }
        handleVisibleCartAtom();
    }, 700);
});

document.addEventListener('on:cart:change', function (e) {
if (e?.detail?.data) {
    theme.globals.cart = e.detail.data;
}
  handleVipCartRules(e);
});

document.addEventListener('collection:color-dot:change', function (e) {
  MarkdownPricePro.run();
});

document.addEventListener('product:color-dot:change', function (e) {
  MarkdownPricePro.run();
});

  function handleVipCartRules(cart) {
    const checkbox = document.querySelector('.stl-vip-chk');

    const vipItem = data?.items.find((cartItem) =>
      cartItem.handle?.includes('priority-vip-processing')
    );

    const vipItems = data?.items.filter((cartItem) =>
      cartItem.handle?.includes('priority-vip-processing')
    );

    const hasRxProduct = data?.items.some((cartItem) =>
      cartItem.tags?.includes('RX')
    );

    if (vipItem && cart.items.length === 1 && !window.justAddedVip) {
      return fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: vipItem.key,
          quantity: 0,
        }),
      })
        .then((res) => res.json())
        .then((updatedCart) => {
          if (checkbox) checkbox.checked = false;
          Cookies.remove('vip_auto_added', { path: '/' });

          document.dispatchEvent(
            new CustomEvent('on:cart:change', {
              detail: { data: updatedCart },
            })
          );
        });
    }

    if (hasRxProduct && vipItems.length) {
      return Promise.all(
        vipItems.map((item) =>
          fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              id: item.key,
              quantity: 0,
            }),
          })
        )
      )
        .then(() => fetch('/cart.js'))
        .then((res) => res.json())
        .then((updatedCart) => {
          if (checkbox) checkbox.checked = false;
          Cookies.remove('vip_auto_added', { path: '/' });

          document.dispatchEvent(
            new CustomEvent('on:cart:change', {
              detail: { data: updatedCart },
            })
          );
        });
    }

    if (vipItems.length > 1) {
      return Promise.all(
        vipItems.slice(1).map((item) =>
          fetch('/cart/change.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              Accept: 'application/json',
            },
            body: JSON.stringify({
              id: item.key,
              quantity: 0,
            }),
          })
        )
      )
        .then(() => fetch('/cart.js'))
        .then((res) => res.json())
        .then((updatedCart) => {
          document.dispatchEvent(
            new CustomEvent('on:cart:change', {
              detail: { data: updatedCart },
            })
          );
        });
    }

    return Promise.resolve();
}

function discountApres() {
    setTimeout(() => {
        const disabled = Cookies.get('apres_discount_disabled') === '1';
        if (disabled) return;

        const apres_items = document.querySelectorAll('.cart-item[data-item-apres-eligible="true"]');
        const apres_img = document.querySelector('.apres-ski-img');

        if (apres_items.length > 0 && !document.body.classList.contains('loop-returns-activated')) {
        

            setCookieWithExpiry("markdown_discount_code", 'APRES', 7);
            setCookieWithExpiry("discount_code", 'APRES', 7);

            if (apres_img) apres_img.style.display = "block";
        } else {
            if(Cookies.get('discount_code')=='APRES' || Cookies.get('markdown_discount_code')=='APRES') {
                Cookies.remove('markdown_object');
                Cookies.remove('markdown_discount_code');
                Cookies.remove('discount_code');
                if (apres_img) apres_img.style.display = "none";
            }
        }
    }, 2000);
}



function checkMysteryPairProduct() {
  const body_tag = document.querySelector('body');
  const men_mp1 = 43630051983407;
  const women_mp1 = 43630052507695;
  const price_mp1 = '$19.45';

  const men_mp2 = 43630052114479;
  const women_mp2 = 43630052638767;
  const price_mp2 = '$22.00';

  const men_mp3 = 43630052278319;
  const women_mp3 = 43630052671535;
  const price_mp3 = '$24.00';

  const mp_handle = document.querySelector('.sr-box-mystery-details');
  if (body_tag.classList.contains("mystery-pair-1")) {
      mp_handle.querySelector('#mystery_men').value = men_mp1;
      mp_handle.querySelector('#mystery_women').value = women_mp1;
      mp_handle.querySelector('.price').innerHTML = price_mp1;
  } else if (body_tag.classList.contains("mystery-pair-2")) {
      mp_handle.querySelector('#mystery_men').value = men_mp2;
      mp_handle.querySelector('#mystery_women').value = women_mp2;
      mp_handle.querySelector('.price').innerHTML = price_mp2;
  } else if (body_tag.classList.contains("mystery-pair-3")) {
      mp_handle.querySelector('#mystery_men').value = men_mp3;
      mp_handle.querySelector('#mystery_women').value = women_mp3;
      mp_handle.querySelector('.price').innerHTML = price_mp3;
  }

  if (document.querySelector('.cart-mystery-pair-btn'))
      document.querySelector('.cart-mystery-pair-btn').removeAttribute('disabled');
}

document.addEventListener('markdown:after-run', function (e) {
  const markdownObject = e.detail?.markdownObject;
  if (markdownObject) {
      new SchemaCorrector(markdownObject);
  }

});

class SchemaCorrector {

  constructor(markdownObject) {
      this.markdownObject = markdownObject;
      this.init();
  }

  init() {
      try {
          if (!theme.globals) return;

          const template = theme.globals.template;

          if (!template.includes('product')) return;

          const schemaElement = document.querySelector('script[type="application/ld+json"][data-product-schema-json]');
          if (!schemaElement) return;

          const schemaJson = JSON.parse(schemaElement.innerText);
          if (!schemaJson) return;

          const schemaType = schemaJson['@type'];

          if (schemaType !== 'ProductGroup' && schemaType !== 'Product') return;

          const user_currency = theme.globals.userCurrency;

          const formatter = new Intl.NumberFormat("en-US", {
              style: "decimal",
              currency: user_currency,
              maximumFractionDigits: 2,
              minimumFractionDigits: 2,
              useGrouping: !1,
          });

          const priceElement = document.querySelector('.product-form [data-product-collections] [data-value="markdown_price"]');
          if (!priceElement) return;

          const parentElement = priceElement.parentElement;

          if (!parentElement.dataset.productCollections) return;

          const collections = parentElement.dataset.productCollections.split(' ');
          const eligibleMarkdown = this.markdownObject.find((markdown) => {
              const eligibleCollections = markdown.markdown_collections.split(',');
              const filteredArray = collections.filter(value => eligibleCollections.includes(value));
              return filteredArray.length > 0;
          });

          if (!eligibleMarkdown) return;

          const mode = eligibleMarkdown.markdown_mode;
          const multiplier = Number(eligibleMarkdown.markdown_multiplier);

          const hasOfferData = schemaJson.hasVariant || schemaJson.offers;
          if (!hasOfferData) return;

          if (schemaJson.hasVariant) {
              schemaJson.hasVariant.map((offersData) => {
                  const offers = offersData.offers;
                  if (offers.discount) return;

                  const compareAtPrice = offers.price;

                  let newPrice = mode === "subtract"
                      ? compareAtPrice - Math.floor(multiplier * theme.globals.currencyConversion[theme.globals.userCurrency])
                      : multiplier * compareAtPrice;

                  offers.price = formatter.format(newPrice);

                  if (compareAtPrice > newPrice) {
                      offers.originalPrice = formatter.format(compareAtPrice);
                      offers.discount = {
                          "@type": "Discount",
                          "amount": (compareAtPrice - newPrice).toFixed(2),
                          "percentOff": Math.round(((compareAtPrice - newPrice) / compareAtPrice) * 100)
                      };
                  }

                  return offers;
              });
          } else {
              const offers = schemaJson.offers;

              if (offers.discount) return;

              const compareAtPrice = offers.price;

              let newPrice = mode === "subtract"
                  ? compareAtPrice - Math.floor(multiplier * theme.globals.currencyConversion[theme.globals.userCurrency])
                  : multiplier * compareAtPrice;

              offers.price = formatter.format(newPrice);

              if (compareAtPrice > newPrice) {
                  offers.originalPrice = formatter.format(compareAtPrice);
                  offers.discount = {
                      "@type": "Discount",
                      "amount": (compareAtPrice - newPrice).toFixed(2),
                      "percentOff": Math.round(((compareAtPrice - newPrice) / compareAtPrice) * 100)
                  };
              }

          }


          // Replace the old JSON-LD script with the updated one
          let updatedSchema = document.createElement("script");
          updatedSchema.type = "application/ld+json";
          updatedSchema.dataset.productSchemaJson = "";
          updatedSchema.classList.add("product-schema-json");
          updatedSchema.textContent = JSON.stringify(schemaJson, null, 2);

          // Remove old script and add the new one
          schemaElement.parentNode.replaceChild(updatedSchema, schemaElement);
      } catch (error) {
          console.log("Error updating product schema", error);
      }
  }

}


(function () {
  function handleFeatureLogic() {
     let featureCopy = {
       cayman: { desktop: "CHECK OUT OUR BEST SELLER", mobile: "" },
       talon: { desktop: "CHECK OUT OUR BEST SELLING SPORTS FRAME", mobile: "BEST SELLING SPORTS FRAME" },
       huron: { desktop: "CHECK OUT OUR BEST SELLING RIMLESS FRAME", mobile: "BEST SELLING RIMLESS FRAME" },
       "green-wolf": { desktop: "Built Exclusively for Golf.", mobile: "" },
       "green wolf": { desktop: "Built Exclusively for Golf.", mobile: "" },
       "tangle-free": { desktop: "Shop our best selling styles with Tangle Free technology", mobile: "" },
       "rival-shift": { desktop: "SHOP OUR NEWEST SEMI-RIMLESS STYLE", mobile: "NEWEST SEMI-RIMLESS STYLE" },
       "country-chic": { desktop: "Check out our limited edition Country Chic frames", mobile: "" },
       allure: { desktop: "CHECK OUT OUR BEST SELLING WOMEN’S COLLECTION", mobile: "WOMEN’S BEST SELLER" },
       peach: { desktop: "Shop Best-selling Tangle Free Aviators", mobile: "" },
       reign: { desktop: "CHECK OUT OUR BEST SELLING ALL-TERRAIN FRAME", mobile: "BEST SELLING ALL-TERRAIN FRAME" },
       apex: { desktop: "CHECK OUT OUR BEST SELLING SPORT FRAME", mobile: "BEST SELLING SPORT FRAME" },
       ace: { desktop: "SHOP OUR BEST SELLING WRAP AROUND STYLE", mobile: "BEST SELLING WRAP AROUND STYLE" },
       classics: { desktop: "CHECK OUT OUR BEST SELLING SUNGLASSES", mobile: "CHECK OUT OUR BEST SELLER" },
       "forged-23xi": {
         desktop: "<img src='https://cdn.shopify.com/s/files/1/0350/5401/files/limited_edition.svg?v=1751995492' class='limited' alt='limited edition'><img src='https://cdn.shopify.com/s/files/1/0350/5401/files/23xI.svg?v=1751995540' class='23xi' alt='23xl'>",
         mobile: ""
       },
       "good-good": {
         desktop: "<img src='https://cdn.shopify.com/s/files/1/0350/5401/files/good-good-badge-desk.svg?v=1755020292' class='desktop-only' alt='Good Good Desk'><img src='https://cdn.shopify.com/s/files/1/0350/5401/files/good-good-badge-mob.svg?v=1755020328' class='mobile-only' alt='Good Good Mob'>",
         mobile: ""
       },
       "sahith-collection": {
         desktop: "<img src='https://cdn.shopify.com/s/files/1/0350/5401/files/sahith-badge-desk.svg?v=1755020292' class='desktop-only' alt='Sahith Desk'><img src='https://cdn.shopify.com/s/files/1/0350/5401/files/sahith-badge-mob.svg?v=1755020328' class='mobile-only' alt='Sahith Mob'>",
         mobile: ""
       }
     };
     function updateRecentlyViewedTitle(activeFeature) {
         const titleDiv = document.querySelector('.recently-viewed-title');
         if (!titleDiv || !featureCopy[activeFeature.toLowerCase()]) return;
         const featureKey = activeFeature.toLowerCase();
         const featureData = featureCopy[featureKey];
         const isMobile = window.innerWidth < 768;
         const textToUse = isMobile && featureData.mobile && featureData.mobile.trim() !== ""
             ? featureData.mobile
             : featureData.desktop;
         document.querySelector(".recently-viewed-bar")?.classList.add(activeFeature);
         if (activeFeature === "forged-23xi" || activeFeature === "good-good" || activeFeature === "sahith-collection") {
             titleDiv.innerHTML = textToUse;
         } else {
             titleDiv.textContent = textToUse;
         }
     }
      function getQueryParam(name) {
          const urlParams = new URLSearchParams(window.location.search);
          return urlParams.get(name)?.toLowerCase() || null;
      }
  
      function setCookie(name, value) {
          document.cookie = `${name}=${value}; path=/; max-age=${48 * 60 * 60}`;
      }
      function deleteCookie(name) {
          document.cookie = `${name}=; path=/; max-age=0`;
          document.cookie = `${name}=; path=/; domain=.shadyrays.com; max-age=0`; // use whatever DevTools shows
      }
      function getCookie(name) {
          const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
          return match ? match[2] : null;
      }
        const allowedCollections = [

        'huron',

        'talon',

        'cayman',

        'reign',

        'allure',

        'green-wolf',

        'green wolf',

        'apex',

        'classics',

        'ace',

        'rival-shift'

      ];
      let featureValue = getQueryParam("feature");
      const storedFeature = getCookie("feature");

      if (storedFeature) {
          const normalized = storedFeature.trim().toLowerCase();
          if (!allowedCollections.includes(normalized)) {
              deleteCookie("feature");
          }
      }

      if (
          featureValue &&
          featureValue !== storedFeature
      ) {
          if (
              ["huron", "talon", "cayman","reign","allure","green-wolf","apex","classics","ace",'rival-shift'].includes(featureValue)
          ) {
              setCookie("feature", featureValue);
          } else if (
              document.body.classList.contains('show-alt-path')
          ) {
              if (!storedFeature && featureValue) {
                  setCookie("feature", featureValue);
              }
          }
      }

      const isDirectLanding =

        document.referrer === '' ||

        !document.referrer.includes(window.location.hostname);

      const isAllowedTemplate =

        document.body.classList.contains('template-collection') ||

        document.body.classList.contains('template-product');
      if (typeof collectionTitle !== 'undefined') {
      var title = collectionTitle.toLowerCase().replace(/\s+/g, '-');
      }
      if (

        isDirectLanding &&

        isAllowedTemplate &&

        allowedCollections.includes(title)

      ) {

        setCookie('feature', title);
        // ShadyRaysGA4.track('adaptive_shopping_experience');
      }
      const activeFeature = getCookie("feature");
      const homepageBanner = document.querySelector(".sr-homepage-banner") || document.querySelector(".anniversary-banner") || document.querySelector(".women-banner");
      const homepageBanner_mob = document.querySelector(".sr-homepage-banner-mob");
      const homepageBanner_vip = document.querySelector(".sr-homepage-video");
      const stickyFilter = document.querySelector(".collection-shadeshop-filter-wrapper");
  
      document.querySelectorAll(".feature-section, .recent-btn").forEach(section => {
          section.classList.add("hidden");
      });
      if (activeFeature) {

        const featureKey = activeFeature?.toLowerCase();
    
        (function () {
            const featureMap = {
                "cayman": { desk: "SR-Cayman-desk-new", deskExt: "png", mob: "SR-Cayman-mob-new", mobExt: "png", url: "cayman", text: "Shop Cayman" },
                "talon": { desk: "SR-Talon-desk-new", deskExt: "png", mob: "SR-talon-mob-new", mobExt: "png", url: "talon", text: "Shop Talon" },
                "huron": { desk: "SR-Huron-desk-new", deskExt: "png", mob: "SR-Huron-mob-new", mobExt: "png", url: "huron", text: "Shop Huron" },
                "green-wolf": { desk: "SR-Golf-desk", deskExt: "png", mob: "SR-Golf-mob", mobExt: "png", url: "green-wolf", text: "Shop Green Wolf" },
                "green wolf": { desk: "SR-Golf-desk", deskExt: "png", mob: "SR-Golf-mob", mobExt: "png", url: "green-wolf", text: "Shop Green Wolf" },
                "tangle-free": { desk: "SR-Tangle-Free-desk", deskExt: "svg", mob: "SR-Tangle-Free-mob", mobExt: "svg", url: "tangle-free", text: "Shop Tangle Free" },
                "country-chic": { desk: "SR-Country-Chic-desk", deskExt: "svg", mob: "SR-Country-Chic-mob", mobExt: "png", url: "country-chic", text: "Shop Country Chic" },
                "allure": { desk: "SR-Allure-desk-new", deskExt: "png", mob: "SR-Allure-mob-new", mobExt: "png", url: "allure", text: "Shop Allure" },
                "peach": { desk: "SR-Peach-Aviator-desk", deskExt: "svg", mob: "SR-Peach-Aviator-mob", mobExt: "svg", url: "peach", text: "Shop Peach" },
                "reign": { desk: "SR-Reign-desk-new", deskExt: "png", mob: "SR-Reign-mob-new", mobExt: "png", url: "reign", text: "Shop Reign" },
                "forged-23xi": { desk: "SR-23xi-desk", deskExt: "svg", mob: "SR-23xi-mob", mobExt: "png", url: "forged-23xi", text: "Shop Forged 23xi" },
                "apex": { desk: "SR-Apex-desk-new", deskExt: "png", mob: "SR-Apex-mob-new", mobExt: "png", url: "apex", text: "Shop Apex" },
                "classics": { desk: "SR-Classics-desk-new", deskExt: "png", mob: "SR-Classics-mob-new", mobExt: "png", url: "classics", text: "Shop Classics" },
                "ace": { desk: "SR-Ace-desk-new", deskExt: "png", mob: "SR-Ace-mob-new", mobExt: "png", url: "ace", text: "Shop Ace" },
                "rival-shift": { desk: "SR-rivals-desk-new", deskExt: "png", mob: "SR-rivals-mob-new", mobExt: "png", url: "rival-shift", text: "Shop Rival Shift" }
            };
    
            const hasAltBanner = featureKey && featureMap[featureKey];
            let banner = document.querySelector('.home-banner-shadeshop');
    
            if (hasAltBanner) {
                homepageBanner?.classList.add("hidden");
                homepageBanner_vip?.classList.add("hidden");
                homepageBanner_mob?.classList.add("hidden");
            } else {
              if (homepageBanner) homepageBanner.style.opacity = "1";
            }
    
            if (!hasAltBanner || !banner) return;
    
            const { desk, deskExt, mob, mobExt, url, text } = featureMap[featureKey];
    
            const updateImageSources = (img, baseName, ext) => {
                if (!img) return;
                const widths = ["340w","480w","740w","980w","1200w","1400w","1440w","1800w"];
                img.src = `//shadyrays.com/cdn/shop/files/${baseName}.${ext}`;
                img.srcset = widths.map(w =>
                    `//shadyrays.com/cdn/shop/files/${baseName}.${ext}?&width=${w.replace('w','')} ${w}`
                ).join(", ");
                img.sizes = "100vw";
            };
    

    
            const buttons = banner.querySelectorAll('.text-overlay__button-row a');

            if (buttons[0]) {
                buttons[0].href = `/collections/${url}`;
                buttons[0].textContent = text;
            }

            if (buttons[1]) {
                buttons[1].href = '/collections/men-all-styles';
                buttons[1].textContent = 'Shop All';
            }
    
            banner.classList.remove("hidden");
        })();
    
    
        const altBanner = document.querySelector('.sr-alt-path-banner');
        const hasAltBanner = document.querySelector('.sr-alt-path-banner img[src*="SR-"]');
    
        if (activeFeature === 'good-good') {
            document.querySelector(`.sr-good-good`)?.classList.remove("hidden");
            document.querySelector('.shop-good-good-golf-editions')?.classList.remove("hidden");
    
        } else if (activeFeature === 'sahith-collection') {
            document.querySelector(`.sr-sahith-collection`)?.classList.remove("hidden");
            document.querySelector('.shop-sahith-editions')?.classList.remove("hidden");
    
        } else if (!hasAltBanner) {
            homepageBanner?.classList.remove("hidden");
            homepageBanner_vip?.classList.remove("hidden");
            homepageBanner_mob?.classList.remove("hidden");
            altBanner?.classList.add("hidden");

            if (homepageBanner) homepageBanner.style.opacity = "1";
            if (homepageBanner_vip) homepageBanner_vip.style.opacity = "1";
            if (homepageBanner_mob) homepageBanner_mob.style.opacity = "1";
        }
        if (altBanner) {
          if (featureKey === 'huron') {
            altBanner.classList.add('text-black-banner');
          } else {
            altBanner.classList.remove('text-black-banner');
          }
        }
        updateRecentlyViewedTitle(activeFeature);
    
        const discountNote = document.querySelector('.top-discount-note');
        const slideshows = document.querySelectorAll('.section-sr-slideshow');
        if (discountNote && slideshows.length > 0) {
            const lastSlideshow = slideshows[slideshows.length - 1];
            lastSlideshow.parentNode.insertBefore(discountNote, lastSlideshow.nextSibling);
        }
    
        document.querySelector(`.shop-${activeFeature}`)?.classList.remove("hidden");
    
        if(activeFeature == 'green-wolf' || activeFeature == 'green wolf') {
            document.querySelector('.shop-men')?.classList.remove("hidden");
            document.body.classList.add('feature-green-wolf')
        }
    
        document.querySelector(".recently-viewed-bar")?.classList.remove("hidden");
    
        if (stickyFilter) {
            stickyFilter.classList.add("rel-sticky-filter");
        }
    
        const featureCard = document.querySelector(`.card-${activeFeature}`);
        if (featureCard) {
            featureCard.style.order = "-1";
            featureCard.classList.remove("hidden");
        }


        if (activeFeature === "huron" || activeFeature === "reign" ) {
          const allureCard = document.querySelector(".card-allure");
          if (allureCard) allureCard.classList.add("hidden");
        }

        if (activeFeature === "tangle-free") {
          const allureCard = document.querySelector(".card-allure");
          if (allureCard) allureCard.style.order = "-1";

          const cayman = document.querySelector(".card-cayman");
          if (cayman) cayman.classList.add("hidden");
        }
        if (activeFeature === "classics") {
          const allureCard = document.querySelector(".card-talon");
          if (allureCard) allureCard.style.order = "-1";

          const banner = document.querySelector(".home-banner-shadeshop");
            if(banner ) {

          
              const buttons = banner.querySelectorAll(".text-overlay__button");
            
              if (buttons[0]) {
                buttons[0].textContent = "Shop Classics";
                buttons[0].setAttribute("href", "/collections/classics");
              }
            
              if (buttons[1]) {
                buttons[1].textContent = "Shop All";
                buttons[1].setAttribute("href", "/collections/men-all-styles");
              }
            }
        }
        if (activeFeature === "apex") {
          document.querySelector(".card-allure")?.classList.add("hidden");
          document.querySelector(".card-tangle-free")?.classList.add("hidden");

          const reignCard = document.querySelector(".card-reign");
          if (reignCard) {
              reignCard.classList.remove("hidden");
              reignCard.style.order = "0";
          }
        }
        if (activeFeature === "cayman") {
          const cayman = document.querySelector(".card-cayman");
          if (cayman) {
              cayman.classList.remove("hidden");
              cayman.style.order = "-1";
          }
          const allureCard = document.querySelector(".card-allure");
          if (allureCard) allureCard.classList.add("hidden");
        }
        if (activeFeature === "ace") {
          const cayman = document.querySelector(".card-ace");
          if (cayman) {
              cayman.classList.remove("hidden");
              cayman.style.order = "-1";
          }
          const caymanCard = document.querySelector(".card-cayman");
          if (caymanCard) {
            caymanCard.classList.remove("hidden");
          }
          const allureCard = document.querySelector(".card-allure");
          if (allureCard) allureCard.classList.add("hidden");
          
          const tanglefreeCard = document.querySelector(".card-tangle-free");
          if (tanglefreeCard) {
            tanglefreeCard.classList.add("hidden");
          }
        }
        if (activeFeature === "rival-shift") {
          const cayman = document.querySelector(".card-rival-shift");
          if (cayman) {
              cayman.classList.remove("hidden");
              cayman.style.order = "-1";
          }
          
          const caymanCard = document.querySelector(".card-cayman");
          if (caymanCard) {
            caymanCard.classList.remove("hidden");
          }

          const allureCard = document.querySelector(".card-allure");
          if (allureCard) allureCard.classList.add("hidden");
          
          const tanglefreeCard = document.querySelector(".card-tangle-free");
          if (tanglefreeCard) {
            tanglefreeCard.classList.add("hidden");
          }
        }
        if (activeFeature === "forged-23xi") {
          document.querySelector(".card-tangle-free")?.classList.add("hidden");

          const cayman = document.querySelector(".card-cayman");
          if (cayman) {
              cayman.classList.remove("hidden");
              cayman.style.order = "3";
          }

          const allureCard = document.querySelector(".card-allure");
          if (allureCard) allureCard.style.order = "4";

          if (document.body.classList.contains('template-suffix-partner-forged-23xi')) {
              document.querySelectorAll(
                  '.forged-23xi-banner, .forged-2xi-logo, .forged-collage, .forged-insta, .forged-23xi-rv, .forged-23xi-cc'
              ).forEach(el => el.classList.remove('hidden'));

              document.querySelectorAll(
                  '.template-suffix-partner-forged-23xi .home-colorrush-section, .template-suffix-partner-forged-23xi .section-sr-homepage-replacement, .template-suffix-partner-forged-23xi .we-got-you-section'
              ).forEach(el => {
                  el.style.display = 'block';
                  el.classList.remove('desktop-only');
              });

              document.querySelectorAll(
                  '.partner-details-section, .partner-gallery-section, .partner-products-heading, .partner-slide-image'
              ).forEach(el => el.classList.add('hidden'));
          }
        }

        if (activeFeature === "allure") {
          const tangleCard = document.querySelector(".card-tangle-free");
          if (tangleCard) {
              tangleCard.style.order = "0";
              tangleCard.style.marginLeft = '10px';

              const updateTangleCardMargin = () => {
                  tangleCard.style.marginRight = window.innerWidth > 1280 ? '20px' : '';
              };

              updateTangleCardMargin();
              window.addEventListener('resize', updateTangleCardMargin);
          }
        }

        if(activeFeature === "peach") {
          const tangleCard = document.querySelector(".card-tangle-free");
          const allureCard = document.querySelector(".card-allure");

          document.querySelector(".card-talon")?.classList.add("hidden");
          document.querySelector(".pdp-tangle-free-aviator---peach-polarized .review-section")?.classList.remove("hidden");
          document.querySelector(".pdp-tangle-free-aviator---peach-polarized .hp-men-women-bestseller")?.classList.remove("hidden");

          if (allureCard) allureCard.style.order = "-1";
          if (tangleCard) tangleCard.style.order = "0";
        }

        if (activeFeature === "country-chic") {
          document.querySelector(".card-talon")?.classList.add("hidden");
          document.querySelector(".card-classics")?.style.setProperty("order","2");
          document.querySelector(".sr-tab2 .hp-tile-23xi")?.classList.remove('hidden');
        }

        if (activeFeature === 'green-wolf' || activeFeature == 'green wolf') {
          const heading = document.querySelector('.feature-text-paired .text-block h2');
          if (heading) heading.textContent = 'These are perfect for the golf course. They protect my eyes from the sun but still allow me to read the green.';

          const quoteName = document.querySelector('.feature-text-paired .quote-name');
          if (quoteName) quoteName.textContent = '-mark g.';

          document.querySelector('.green-wolf-home-tile')?.classList.add("hidden");

          const reviewBtn = document.querySelector('.review-section .button-row a.btn');
          if (reviewBtn) {
              reviewBtn.textContent = 'Shop Golf';
              reviewBtn.setAttribute('href', '/collections/green-wolf');
          }
        }

        const reviewSection = document.querySelector(".review-section .image-overlap__image .fade-in-up");
        const reviewSection_mob = document.querySelector(".review-mobile-model");

        if (reviewSection && activeFeature !== 'good-good' && activeFeature !== 'sahith-collection') {
          const featureSlug = `${activeFeature}-rv`;
          const newImg = `
              <img 
                  src="//shadyrays.com/cdn/shop/files/${featureSlug}.png?v=1781910281&width=1140" 
                  width="1140" height="938" loading="lazy"
                  sizes="(min-width: 1600px) 800px, (min-width: 768px) 50vw, 100vw" 
                  srcset="//shadyrays.com/cdn/shop/files/${featureSlug}.png?v=1781910281&width=425 340w,
                          //shadyrays.com/cdn/shop/files/${featureSlug}.png?v=1781910281&width=600 480w,
                          //shadyrays.com/cdn/shop/files/${featureSlug}.png?v=1781910281&width=925 740w,
                          //shadyrays.com/cdn/shop/files/${featureSlug}.png?v=1781910281&width=1225 980w,
                          //shadyrays.com/cdn/shop/files/${featureSlug}.png?v=1781910281&width=1425 1140w"
                  class="theme-img"
              >
          `;

          reviewSection.innerHTML = newImg;
          if (reviewSection_mob) reviewSection_mob.innerHTML = newImg;
        }
    }
      else {
           const body = document.body;
          // document.body.classList.add('FX-Home-C'); 
          const currentClass = ["FX-Home-A", "FX-Home-B", "FX-Home-C"].find(cls => body.classList.contains(cls));  
          const ratingDiv = document.querySelector(".home-ab .text-overlay__reveal .has-motion");
          if(ratingDiv) {
              ratingDiv.style.opacity = 0;
          }
      
          function applyHomeContent() {
                  const headings = document.querySelectorAll(".home-ab .text-overlay__title");
                  const descriptions = document.querySelectorAll(".home-ab .text-overlay__rte p");                               
                  const contentMap = {
                  "FX-Home-A": {
                      heading: "Lose or Break them?",
                      descriptionHTML: `We replace them. <a href="/pages/protection-programs" title="Protection Programs" aria-label="Learn more">Learn more</a>`,
                      showRating: false,
                      extraDescClass: false
                  },
                  "FX-Home-B": {
                      heading: "Trusted by Millions",
                      descriptionHTML: " ",
                      showRating: true,
                      extraDescClass: false
                  },
                  "FX-Home-C": {
                      heading: "Uncompromising Quality",
                      descriptionHTML: "Starting at $59",
                      showRating: false,
                      extraDescClass: true
                  }
                  };
                          
                  const content = contentMap[currentClass];
                  if(content){
                      headings.forEach(h => h.innerText = content.heading);
                  
                      descriptions.forEach(p => {
                      p.innerHTML = content.descriptionHTML;
                      if (content.extraDescClass) {
                          p.classList.add("homeC-long-desc");
                      } else {
                          p.classList.remove("homeC-long-desc");
                      }
                      });
                  
                      if (ratingDiv) {
                      ratingDiv.style.opacity = content.showRating ? "1" : "0";
                      }
                  }
          }
          if(currentClass){
              applyHomeContent();
          }
          if (homepageBanner) {
              homepageBanner.style.opacity = "1";
          }
          if (homepageBanner_mob) {
              homepageBanner_mob.style.opacity = "1";
          }
          if (stickyFilter) {
              stickyFilter.classList.remove("rel-sticky-filter");
          }
              const el = document.querySelector('.template-suffix-green-wolf');
              if (el) {
              el.style.opacity = '1';
              }
      }
   if (document.body.classList.contains('template-collection') && typeof collectionTitle !== 'undefined') {
          document.querySelector(".recently-viewed-banner")?.classList.add("hidden");
          var title = collectionTitle.toLowerCase().replace(/\s+/g, '-');
          const FEATURE_COOKIE = "feature";
          const FIRST_VISIT_COOKIE = "FirstVisitCollection";
          const COOKIE_MAX_AGE = 72 * 60 * 60;
          const hasFeatureCookie = getCookie(FEATURE_COOKIE);
          const hasFirstVisitCookie = getCookie(FIRST_VISIT_COOKIE) || getCookie("feature");
          if (hasFirstVisitCookie) {
              setCookie(FIRST_VISIT_COOKIE, title);
          }
          const bodyClass = [...document.body.classList].find(cls => cls.startsWith('feature-'));
          const activeFeature = bodyClass?.replace('feature-', '') || getCookie("feature")|| getCookie(FIRST_VISIT_COOKIE);
          if (title === 'sahith') {
              title = 'sahith-collection';
          }
            document.querySelector(".hp-men-women-bestseller")?.classList.remove("hidden");
            const reviewSection = document.querySelector(".review-section .image-overlap__image .fade-in-up");
            const reviewSection_mob = document.querySelector(".review-mobile-model");
           if (reviewSection) {
                  const featureSlug = `rev-img`;
                  const newImg = `
                      <img 
                          src="//shadyrays.com/cdn/shop/files/${featureSlug}.png?v=1699912370&amp;width=1140" 
                          alt="" 
                          width="1140" 
                          height="938" 
                          loading="lazy" 
                          sizes="(min-width: 1600px) 800px, (min-width: 768px) 50vw, 100vw" 
                          srcset="//shadyrays.com/cdn/shop/files/${featureSlug}.png?v=1699912370&amp;width=425 340w, 
                                  //shadyrays.com/cdn/shop/files/${featureSlug}.png?v=1699912370&amp;width=600 480w, 
                                  //shadyrays.com/cdn/shop/files/${featureSlug}.png?v=1699912370&amp;width=925 740w, 
                                  //shadyrays.com/cdn/shop/files/${featureSlug}.png?v=1699912370&amp;width=1225 980w, 
                                  //shadyrays.com/cdn/shop/files/${featureSlug}.png?v=1699912370&amp;width=1425 1140w" 
                          class="theme-img"
                      >
                  `;
                  reviewSection.innerHTML = newImg;
                  if (reviewSection_mob) {
                      reviewSection_mob.innerHTML = newImg;
                  }
              }
             
            document.querySelector(".two-cols")?.classList.add("hidden");
            document.querySelector(".review-section")?.classList.remove("hidden");
          if (title === activeFeature && !document.body.classList.contains('template-product') && title != 'golf-elite' && title != 'fishing-collection') {
            setCookie("feature", activeFeature);
            updateRecentlyViewedTitle(activeFeature);  
            document.querySelector(".we-got-you-section.mobile-only")?.classList.add("hidden");
            document.querySelector(".trust-bar")?.classList.remove("hidden");
            document.querySelector(".section-sr-homepage-colorush")?.style.setProperty("display", "block");
             if (reviewSection) {
                  const featureSlug = `${activeFeature}-rv`;
                  const newImg = `
                      <img 
                          src="//shadyrays.com/cdn/shop/files/${featureSlug}.png?v=1699912370&amp;width=1140" 
                          alt="" 
                          width="1140" 
                          height="938" 
                          loading="lazy" 
                          sizes="(min-width: 1600px) 800px, (min-width: 768px) 50vw, 100vw" 
                          srcset="//shadyrays.com/cdn/shop/files/${featureSlug}.png?v=1699912370&amp;width=425 340w, 
                                  //shadyrays.com/cdn/shop/files/${featureSlug}.png?v=1699912370&amp;width=600 480w, 
                                  //shadyrays.com/cdn/shop/files/${featureSlug}.png?v=1699912370&amp;width=925 740w, 
                                  //shadyrays.com/cdn/shop/files/${featureSlug}.png?v=1699912370&amp;width=1225 980w, 
                                  //shadyrays.com/cdn/shop/files/${featureSlug}.png?v=1699912370&amp;width=1425 1140w" 
                          class="theme-img"
                      >
                  `;
                  reviewSection.innerHTML = newImg;
                  if (reviewSection_mob) {
                      reviewSection_mob.innerHTML = newImg;
                  }
              }
              if (activeFeature == 'tangle-free') {
                  document.querySelectorAll(
                      '.tangle-free-models, .comfy-design-col2.mobile-only, .tangle-hair-seciton, .comfy-design-section.desktop-only'
                    ).forEach(el => {
                      el.style.display = 'none';
                    });
              }
              if(activeFeature == 'green-wolf' || activeFeature == 'green wolf') {
                  const adBarItems = document.querySelectorAll('.gf-green-wolf .collection-ad_bar .flex.items-center');

                  if (adBarItems.length < 3) return;
                
                  const newImages = [
                    {
                      src: 'https://shadyrays.com/cdn/shop/files/RATING.png', 
                      alt: 'RATING'
                    },
                    {
                      src: 'https://shadyrays.com/cdn/shop/files/CUSTOMER.png', 
                      alt: 'CUSTOMER'
                    },
                    {
                      src: 'https://shadyrays.com/cdn/shop/files/BBA.png', 
                      alt: 'BBA'
                    }
                  ];
                
                  adBarItems.forEach((item, index) => {
                    const img = item.querySelector('img');
                    if (img && newImages[index]) {
                      img.src = newImages[index].src;
                      img.alt = newImages[index].alt;
                    }
                  });
                      const textBlockHeading = document.querySelector('.feature-text-paired .text-block h2');
                      if (textBlockHeading) {
                        textBlockHeading.textContent = 'These are perfect for the golf course. They protect my eyes from the sun but still allow me to read the green.';
                      }
                    
                      const quoteName = document.querySelector('.feature-text-paired .quote-name');
                      if (quoteName) {
                        quoteName.textContent = '-MARK G.';
                      }
                      const majorTitleEl = document.querySelector('.majortitle');
                      if (majorTitleEl) {
                      majorTitleEl.textContent = 'Semi-Polarized Lenses';
                      }

                  document.querySelector('.shop-men')?.classList.remove("hidden");
                  document.querySelector('.green-wolf-home-tile')?.classList.add("hidden");
      
              } else {
                  const el = document.querySelector('.template-suffix-green-wolf.template-collection');
                  if (el) {
                  el.style.opacity = '1';
                  }
              }
              if(activeFeature == 'good-good') {
                  document.querySelectorAll(
                      '.colorush-goodgood.feature-good-good .ab-banner-rtb-desk,' +
                      '.colorush-goodgood.feature-good-good .ab-banner-rtb-mob,' +
                      '.colorush-goodgood.feature-good-good .ab-golf-banner,' +
                      '.colorush-goodgood.feature-good-good .ab-gold-likes,' +
                      '.colorush-goodgood.feature-good-good .ab-golf-custom,' +
                      '.colorush-goodgood.feature-good-good .ab-home-banner,' +
                      '.colorush-goodgood.feature-good-good .ab-text'
                  ).forEach(el => {
                      el.style.display = 'block';
                  });
                  document.querySelectorAll(
                  '.colorush-goodgood.feature-good-good .good-good-banner,' +
                  '.colorush-goodgood.feature-good-good .golf-ab-bn-video'
                  ).forEach(el => {
                      el.style.display = 'none';
                  });
              }
              if(activeFeature == 'sahith-collection') {
                  document.querySelectorAll(
                      '.colorush-sahith.feature-sahith-collection .ab-banner-rtb-desk,' +
                      '.colorush-sahith.feature-sahith-collection .ab-banner-rtb-mob,' +
                      '.colorush-sahith.feature-sahith-collection .ab-golf-banner,' +
                      '.colorush-sahith.feature-sahith-collection .ab-gold-likes,' +
                      '.colorush-sahith.feature-sahith-collection .ab-golf-custom,' +
                      '.colorush-sahith.feature-sahith-collection .ab-home-banner,' +
                      '.colorush-sahith.feature-sahith-collection .ab-text'
                  ).forEach(el => {
                      el.style.display = 'block';
                  });

                  document.querySelectorAll(
                      '.colorush-sahith.feature-sahith-collection .sahith-bannner-default,' +
                      '.colorush-sahith.feature-sahith-collection .golf-ab-bn-video'
                  ).forEach(el => {
                      el.style.display = 'none';
                  });
              }
              if(activeFeature == 'country-chic') {
                  document.querySelector('.sr-country-chic')?.classList.remove("hidden");
                  document.querySelector('.collection-new-ui')?.classList.add("hidden");
                   const bannerOverlay = document.querySelector(".collection-banner-overlay .text-white");
                  const rtbsSection = document.querySelector(".collection-rtbs-section.country-chic");

                  if (bannerOverlay) {
                      const img = document.createElement("img");
                      img.src = "https://cdn.shopify.com/s/files/1/0350/5401/files/country-chic-rtb.svg?v=1756224352";
                      img.alt = "Country Chic RTB";
                      img.classList.add("country-chic-rtb", "desktop-only");
                      bannerOverlay.appendChild(img);
                  }

                  function handleResponsive() {
                      if (window.innerWidth <= 768) {
                      if (rtbsSection) {
                          rtbsSection.style.display = "block";
                          rtbsSection.querySelectorAll(".desktop-only").forEach(el => el.classList.remove("desktop-only"));
                      }
                      } else {
                      if (rtbsSection) {
                          rtbsSection.style.display = "none";
                      }
                      }
                  }

                  handleResponsive();
                  window.addEventListener("resize", handleResponsive);
              }
          }
          else {
              document.querySelector(".recently-viewed-banner")?.classList.remove("hidden");
              const el = document.querySelector('.template-suffix-green-wolf');
              if (el) {
              el.style.opacity = '1';
              }
          }
      // }, 1500); 
      }  
      else {
          const el = document.querySelector('.template-suffix-green-wolf');
          if (el) {
          el.style.opacity = '1';
          }
      }
       if (document.body.classList.contains('template-product') && typeof collectionTitle !== 'undefined') {
         function getCookie(name) {
            const match = document.cookie.match(new RegExp(`(^| )${name}=([^;]+)`));
            return match ? match[2] : null;
        }
          document.querySelector(".recently-viewed-banner")?.classList.add("hidden");
          var title = collectionTitle.toLowerCase().replace(/\s+/g, '-');
          const ALLOWED_COLLECTIONS = ['huron', 'talon', 'reign', 'cayman','classics'];
          const FEATURE_COOKIE = "feature";
          const FIRST_VISIT_COOKIE = "FirstVisitCollection";
          const COOKIE_MAX_AGE = 72 * 60 * 60;
          const hasFeatureCookie = getCookie(FEATURE_COOKIE);
          const hasFirstVisitCookie = getCookie(FIRST_VISIT_COOKIE) || getCookie("feature");
          if (hasFirstVisitCookie) {
              setCookie(FIRST_VISIT_COOKIE, title);
              setCookie(FEATURE_COOKIE, title);
          }
     }
  }
   

  setTimeout(handleFeatureLogic, 400);
})();


document.addEventListener("DOMContentLoaded", function () {
  handleMysteryPairAddToCart();

  setTimeout(() => {
    loadVIPProcessing();
    const vip_cart = document.querySelector('input[data-hardcase-handle="priority-vip-processing"]');
    if (document.body.classList.contains('loop-returns-activated') && vip_cart) {
        const cart_vip_pro = document.querySelector('details.vip-processing');
        vip_cart.checked = false;
        vip_cart.dispatchEvent(new Event('change', { bubbles: true }));
        if(cart_vip_pro) {
          cart_vip_pro.style.display = "none";
        }
    }
  }, 1000);

  function formatYotpoReviewNumber() {
      const elements = document.querySelectorAll('.yotpo-sr-bottom-line-text');
    
      elements.forEach(el => {
        const text = el.textContent;
        const match = text.match(/\d{4,}/); // Match 4+ digit numbers
        if (match) {
          const originalNumber = match[0];
          const formatted = Number(originalNumber).toLocaleString();
          el.textContent = text.replace(originalNumber, formatted);
        }
      });
    }
    
    const observer = new MutationObserver((mutations) => {
      mutations.forEach(() => {
        formatYotpoReviewNumber();
      });
    });
    
    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
    formatYotpoReviewNumber();

});

function handleMysteryPairAddToCart() {
const mysteryPair = document.querySelector(".mystery-pair-new");
if (!mysteryPair) return;

const variantSelector = mysteryPair.querySelectorAll('.mystery-pair__selector input[type="radio"]');
const addToCartBtn = mysteryPair.querySelector(".mystery-pair__add-to-cart");
const priceEl = mysteryPair.querySelector(".mystery-pair__price .money") || mysteryPair.querySelector(".mystery-pair__price");
const compareEl = mysteryPair.querySelector(".mystery-pair__compare .money");

function addVariantToCart(variantId) {
  fetch("/cart/add.js", {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      "Accept": "application/json"
    },
    body: JSON.stringify({
      id: variantId,
      quantity: 1
    })
  })
    .then(res => res.json())
    .then(data => {

      document.dispatchEvent(new CustomEvent('on:cart:change', {
        bubbles: true,
        detail: { data }
      }));

      mysteryPair.style.display = "none";
      if (window.location.pathname === '/cart') {
        window.location.reload();
      }
    })
    .catch(err => console.error("Add to cart failed", err));
}

variantSelector.forEach(input => {
  input.addEventListener('change', function () {
    const variantId = mysteryPair.querySelector('.mystery-pair__selector input[type="radio"]:checked').value;

    fetch(`/variants/${variantId}.js`)
      .then(res => res.json())
      .then(variant => {
        priceEl.textContent = `$${(variant.price / 100).toFixed(2)}`;

        if (variant.compare_at_price) {
          if (compareEl) {
            compareEl.textContent = `$${(variant.compare_at_price / 100).toFixed(2)}`;
            compareEl.parentElement.style.display = 'inline-block';
          }
        } else {
          if (compareEl) {
            compareEl.parentElement.style.display = 'none';
          }
        }
      })
      .catch(err => console.error('Failed to fetch variant', err));
  });
});

addToCartBtn.addEventListener("click", function () {
  const variantId = mysteryPair.querySelector('.mystery-pair__selector input[type="radio"]:checked').value;
  addVariantToCart(variantId);
});

document.addEventListener('on:cart:change', (e) => {
  const cart = e.detail?.data;
  const items = cart?.items;

  if (items && items.length === 2) {
    const hasVIP = items.some(item => item.handle === 'priority-vip-processing');
    const hasMystery = items.some(item => item.handle === 'mystery-pair');

    if (hasVIP && hasMystery) {
      fetch('/cart/clear.js', { method: 'POST' })
        .then(() => {
          document.dispatchEvent(new CustomEvent('on:cart:change', {
            bubbles: true,
            cancelable: false,
            detail: { data: null }
          }));
        });
    }
    const priorityItems = items.filter(item =>
            item.product_type &&
            item.product_type.toLowerCase() === 'processing'
          );
    if (priorityItems?.length > 1) {
            const extraVipItems = priorityItems.slice(1);
            Promise.all(
              extraVipItems.map(vipItem =>
                fetch('/cart/change.js', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    id: vipItem.key,
                    quantity: 0
                  })
                })
              )
            )

              .then(() => {
                document.dispatchEvent(
                  new CustomEvent('on:cart:change', {

                    bubbles: true,

                    cancelable: false

                  })
                );
              })

              .catch(err => {
                console.error('Extra VIP removal error:', err);
              });

          }
  }

  setTimeout(() => {
    if (cart?.items) {
      const hasMysteryPair = cart.items.some(item => {
        return item.product_type && item.product_type.toLowerCase() === 'mystery';
      });

      const mysteryBlock = document.querySelector('.mystery-pair-new');
      if (mysteryBlock) {
        mysteryBlock.style.display = hasMysteryPair ? 'none' : 'flex';
        mysteryBlock.classList.toggle('hidden', hasMysteryPair);
      }
    }
     const hasgiftProduct = cart?.items.some(item =>
            item.product_type &&
            item.product_type.toLowerCase() === 'gift card'
          );

          const priorityItems = cart?.items.filter(item =>
            item.product_type &&
            item.product_type.toLowerCase() === 'processing'
          );
          
          if (hasgiftProduct) {
            document.querySelector('.vip-processing').style.display = "none"
          }
          if (hasgiftProduct && priorityItems.length > 0) {

            Promise.all(
              priorityItems.map(priorityItem =>
                fetch('/cart/change.js', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    id: priorityItem.key,
                    quantity: 0
                  })
                })
              )
            )
              .then(() => {
                document.dispatchEvent(
                  new CustomEvent('on:cart:change', {
                    bubbles: true,
                    cancelable: false
                  })
                );
              })
              .catch(err => {
                console.error('Priority removal error:', err);
                window.location.reload();
              });
          }

          const hasRxProduct = cart?.items.some(item =>
            item.tags && item.tags.includes('RX')
          );
          if (hasRxProduct && priorityItems.length > 0) {

            Promise.all(
              priorityItems.map(vipItem =>
                fetch('/cart/change.js', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    id: vipItem.key,
                    quantity: 0
                  })
                })
              )
            )

              .then(() => {
                if (checkbox) checkbox.checked = false;
                Cookies.remove('vip_auto_added', { path: '/' });
                document.dispatchEvent(
                  new CustomEvent('on:cart:change', {
                    bubbles: true,
                    cancelable: false
                  })
                );
              })

              .catch(err => {
                console.error('VIP removal (RX case) error:', err);
                window.location.reload();
              });
          }
          if (priorityItems?.length > 1) {
            const extraVipItems = priorityItems.slice(1);
            Promise.all(
              extraVipItems.map(vipItem =>
                fetch('/cart/change.js', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    id: vipItem.key,
                    quantity: 0
                  })
                })
              )
            )

              .then(() => {
                document.dispatchEvent(
                  new CustomEvent('on:cart:change', {

                    bubbles: true,

                    cancelable: false

                  })
                );
              })

              .catch(err => {
                console.error('Extra VIP removal error:', err);
              });

          }


  }, 300);
});
}
function loadVIPProcessing() {
  const vipProductData = document.getElementById("vip-product-data");
  if (!vipProductData) return;
  const checkbox = document.querySelector(".stl-vip-chk");

  const productData = JSON.parse(vipProductData.textContent);
  const variants = productData.variants || [];

  const vipVariant = variants.find(v => v.title === "PRIORITY");
  const nonVipVariant = variants.find(v => v.title === "STANDARD");
  if (!vipVariant || !nonVipVariant) return;

  const isVipCustomer = Cookies.get('customer_vip') === 'vip' || Cookies.get('SR_PR_kStatus') === 'VIP';
  const selectedVariant = isVipCustomer ? vipVariant : nonVipVariant;
  const formatCurrency = (price) => {
    const currency = theme?.globals?.userCurrency || 'USD';

    let curr = '$';
    if (currency === 'GBP') curr = '£';
    if (currency === 'CAD') curr = '$';

    const priceValue = price / 100;

    if (currency === 'CAD') {
      return `${curr}${Math.round(priceValue)}`;
    }

    return `${curr}${priceValue.toFixed(2)}`;
  };


  const updatePricingDetails = (variant) => {
  const priceDisplay = document.querySelector(".price-display");
  const compareAtPriceDisplay = document.querySelector(".compare-at-price");

  if (!checkbox || !priceDisplay || !compareAtPriceDisplay || !variant) return;

  checkbox.value = variant.id;

  priceDisplay.innerHTML = formatCurrency(variant.price);

  if (variant.compare_at_price && variant.compare_at_price > variant.price) {
    compareAtPriceDisplay.innerHTML = formatCurrency(variant.compare_at_price);
    compareAtPriceDisplay.style.display = "inline";
    priceDisplay.classList.add("sale-price");
  } else {
    compareAtPriceDisplay.style.display = "none";
    priceDisplay.classList.remove("sale-price");
  }
};

  updatePricingDetails(selectedVariant);
  fetch('/cart.js')
  .then(res => res.json())
  .then(cart => {
    addVipItemIfEligible(cart);
      function clearIfSingleHardcaseBundle(cart) {
      if (!cart || !Array.isArray(cart.items) || cart.items.length !== 1) return;

      const item = cart.items[0];
      const isHardCase = item?.product_type === "Hard Case";
      const bundleValue = item?.properties?._hardcase_bundle?.trim();

      if (isHardCase && bundleValue && cart.items.length === 1) {
          fetch('/cart/clear.js', { method: 'POST' })
          .then(() => {
              document.dispatchEvent(new CustomEvent('on:cart:change', {
              bubbles: true,
              cancelable: false,
              detail: { cart }
              }));
          })
          .catch(err => console.error('Error clearing cart:', err));
      }
      }
       setTimeout(() => {
      clearIfSingleHardcaseBundle(cart);
      }, 1000);
  });

}
function addVipItemIfEligible(cart) {
  const isVipCustomer = Cookies.get('customer_vip') === 'vip' || Cookies.get('SR_PR_kStatus') === 'VIP';
  const hasNonVipItems = cart.items.some(item => !item.handle.includes("priority-vip-processing"));
  const alreadyHasVip = cart.items.some(item => item.handle.includes("priority-vip-processing"));

  const checkbox = document.querySelector(".stl-vip-chk");
    if (isVipCustomer && hasNonVipItems && checkbox && alreadyHasVip) {
      if (checkbox) {
        checkbox.disabled = true;
      }
    }
  if (alreadyHasVip) return;
  if (isVipCustomer && hasNonVipItems && checkbox && !alreadyHasVip) {
    if (checkbox) {
      checkbox.checked = true;
      checkbox.disabled = true;
    }

    const formData = {
      id: 43655284031535,
      quantity: 1,
      properties: {
        '_vip_bundle': true
      }
    };

   if (alreadyHasVip) return;
    fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json'
      },
      body: JSON.stringify(formData)
    })
      .then(res => {
        if (!res.ok) throw new Error(`HTTP error: ${res.status}`);
        return res.json();
      })
      .then(data => {  
        const item = document.querySelector('.nonvip');
        if (item) {
          item.querySelector('.cart-item__remove')?.click();
        }

        document.dispatchEvent(new CustomEvent('on:cart:change', {
          bubbles: true,
          detail: { data }
        }));
      })
      .catch(error => {
        console.error("Error adding VIP item:", error);
        window.location.reload();
      });
  }
}
fetch('/cart.js')
  .then((res) => res.json())
  .then((cart) => {
   
    async function updateAllEligibleItems(cart) {
  let updatedCart = null;
  const map = window.SKU_PRICE_ADJUSTMENTS || {};

  for (const item of cart.items) {
    const sku = item.sku ? String(item.sku).trim().toUpperCase() : '';
    const subtractAmount = map[sku];
    if (subtractAmount === undefined) continue;

    const expectedDiscount = subtractAmount.toFixed(2);

    const alreadyAdjusted =
      item.properties?.['Adjusted Base Price'] === 'true' &&
      item.properties?.['Discounted Amount'] === expectedDiscount;

    if (alreadyAdjusted) continue;

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          id: item.key,
          properties: {
            ...(item.properties || {}),
            'Adjusted Base Price': 'true',
            'Discounted Amount': expectedDiscount
          },
        }),
      });
      updatedCart = await response.json();

      document.dispatchEvent(
        new CustomEvent('on:cart:change', { bubbles: true, detail: { data: updatedCart } })
      );
    } catch (err) {
      console.error('Error updating Adjusted Base Price property', err);
    }
  }
}
    updateAllEligibleItems(cart);
  })
  .catch((err) => console.error('Cart fetch failed', err));
document.addEventListener('on:cart:add', (e) => {
checkHighDemand();  
const isVipCustomer = Cookies.get('customer_vip') === 'vip' || Cookies.get('SR_PR_kStatus') === 'VIP';
  const checkbox = document.querySelector(".stl-vip-chk");
  const alreadyAutoAdded = Cookies.get('vip_auto_added') === 'true';
  
  if (checkbox && !isVipCustomer && !checkbox.checked && !alreadyAutoAdded) {
   if(document.querySelector(".cart-item.product-gift-card") == null){
   checkbox.checked = true;
    const event = new Event('change', { bubbles: true });
    checkbox.dispatchEvent(event);
     Cookies.set('vip_auto_added', 'true', { path: '/', expires: 7 });
   }
  } 
  fetch('/cart.js')
    .then(res => res.json())
    .then(cart => {
      if(document.querySelector(".cart-item.product-gift-card") == null){
        addVipItemIfEligible(cart);
      }
      // handleMysteryPair(cart);
      updateAllEligibleItems(cart);
       const hasgiftProduct = cart.items.some(item =>
            item.product_type &&
            item.product_type.toLowerCase() === 'gift card'
          );

          const priorityItems = cart.items.filter(item =>
            item.product_type &&
            item.product_type.toLowerCase() === 'processing'
          );
          if (hasgiftProduct) {
            document.querySelector('.vip-processing').style.display = "none"
          }
          if (hasgiftProduct && priorityItems.length > 0) {

            Promise.all(
              priorityItems.map(priorityItem =>
                fetch('/cart/change.js', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    id: priorityItem.key,
                    quantity: 0
                  })
                })
              )
            )
              .then(() => {
                document.dispatchEvent(
                  new CustomEvent('on:cart:change', {
                    bubbles: true,
                    cancelable: false
                  })
                );
              })
              .catch(err => {
                console.error('Priority removal error:', err);
                window.location.reload();
              });
          }
          const hasRxProduct = cart.items.some(item =>
            item.tags && item.tags.includes('RX')
          );
          if (hasRxProduct && priorityItems.length > 0) {

            Promise.all(
              priorityItems.map(vipItem =>
                fetch('/cart/change.js', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    id: vipItem.key,
                    quantity: 0
                  })
                })
              )
            )

              .then(() => {
                if (checkbox) checkbox.checked = false;
                Cookies.remove('vip_auto_added', { path: '/' });
                document.dispatchEvent(
                  new CustomEvent('on:cart:change', {
                    bubbles: true,
                    cancelable: false
                  })
                );
              })

              .catch(err => {
                console.error('VIP removal (RX case) error:', err);
                window.location.reload();
              });
          }
          if (priorityItems.length > 1) {
            const extraVipItems = priorityItems.slice(1);
            Promise.all(
              extraVipItems.map(vipItem =>
                fetch('/cart/change.js', {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({
                    id: vipItem.key,
                    quantity: 0
                  })
                })
              )
            )
              .then(() => {
              })
              .catch(err => {
                console.error('Extra VIP removal error:', err);
              });

          }
    });
  
    async function updateAllEligibleItems(cart) {
  let updatedCart = null;
  const map = window.SKU_PRICE_ADJUSTMENTS || {};

  for (const item of cart.items) {
    const sku = item.sku ? String(item.sku).trim().toUpperCase() : '';
    const subtractAmount = map[sku];
    if (subtractAmount === undefined) continue;

    const expectedDiscount = subtractAmount.toFixed(2);

    const alreadyAdjusted =
      item.properties?.['Adjusted Base Price'] === 'true' &&
      item.properties?.['Discounted Amount'] === expectedDiscount;

    if (alreadyAdjusted) continue;

    try {
      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
        body: JSON.stringify({
          id: item.key,
          properties: {
            ...(item.properties || {}),
            'Adjusted Base Price': 'true',
            'Discounted Amount': expectedDiscount
          },
        }),
      });
      updatedCart = await response.json();

      document.dispatchEvent(
        new CustomEvent('on:cart:change', { bubbles: true, detail: { data: updatedCart } })
      );
    } catch (err) {
      console.error('Error updating Adjusted Base Price property', err);
    }
  }
}
    document.querySelectorAll('.cart-discount-list').forEach((list) => {
      const seen = new Set();
  
      list.querySelectorAll('.cart-discount').forEach((li) => {
        const titleSpan = li.querySelector('.cart-discount__title');
        if (!titleSpan) return;
  
        const title = titleSpan.textContent.trim();
  
        if (seen.has(title)) {
          li.remove(); 
        } else {
          seen.add(title);
        }
      });
    });
});

function handleVisibleCartAtom() {
  const markdownObject = Cookies.getJSON('markdown_object') || [];
  const vanityMarkdown = markdownObject.find((object) => object.markdown_id === 'vanity-markdown');
  const isBogoVanity = vanityMarkdown && ['BOGO25', 'BOGO2026','MOMBOGO50'].includes(vanityMarkdown.markdown_discount_code);
  const upsellTo2Cart = document.querySelector('.sr-upsell-to-2-cart-wrapper[data-promo="IGOT2"]');
  if (!isBogoVanity) return;

  const visibleItems = document.querySelectorAll('.cart-item[data-item-discount-eligible="true"]');
  let qty = 0;
  visibleItems.forEach((item) => {
       qty = qty + item.dataset.qty;
  });
  let totalQty = qty;
  
  let percentMsg = '50%';
  const newDiv = document.querySelector('.BOGO-VANITY-MESSAGE');
  if (totalQty < 2) {
    newDiv.innerHTML = `
      <div class="sr-upsell-to-2-cart">
        <div class="sr-upsell-to-2-cart-copy">
          <p class="discount-copy">ADD ONE MORE PAIR TO UNLOCK BUY ONE GET ONE <span class="discount-percent">${percentMsg} OFF </span>!</p>
          <div class="shop-men-women flex justify-center">
            <a href="/collections/men-all-styles">Shop Men</a> <span>|</span> <a href="/collections/women-all-styles">Shop Women</a>
          </div>
          <p class="sr-igot2-note"><i>Offer cannot be combined with other coupons.</i></p>
        </div>
      </div>`;
  } else {
    newDiv.innerHTML = `
      <div class="sr-upsell-to-2-get sr-upsell-to-2-cart">Deal Met! You've unlocked <span>${percentMsg} off</span>!</div>`;
      document.querySelector("#mp-cart-block").classList.add("hidden");

  }

}


document.addEventListener('on:cart:add', (e) => {
  handleVisibleCartAtom();
});

// document.addEventListener('cart:change', (e) => {
//   handleVisibleCartAtom();
// });

(function () {
const EVENT_NAME = 'sw:addedtocart';

function attachSwymListener(swat) {
  if (!swat || !swat.evtLayer) return;

  const listener = function (e) {
    const data = e?.detail?.d?.productData;
    if (!data) return;

    document.dispatchEvent(
      new CustomEvent('on:cart:change', {
        bubbles: true,
        detail: {
          variantId: data.variant_id,
        },
      })
    );

    setTimeout(() => {
      const drawer = document.querySelector('.js-cart-drawer');
      if (drawer && typeof drawer.open === 'function') {
        drawer.open();
          const checkbox = document.querySelector(".stl-vip-chk");
          if (checkbox) {
              checkbox.checked = true;
               const event = new Event('change', { bubbles: true });
                  checkbox.dispatchEvent(event);
                  Cookies.set('vip_auto_added', 'true', { path: '/', expires: 7 });
          }
      }
    }, 1200);

  };

  swat.evtLayer.addEventListener(EVENT_NAME, listener);
}

window.SwymCallbacks = window.SwymCallbacks || [];
window.SwymCallbacks.push(function (swat) {
  attachSwymListener(swat);
});

if (window._swat && _swat.evtLayer) {
  attachSwymListener(_swat);
}
})();

//** MYSTERY PAIR LOGIC END */

//** Retention Customer Homepage starts */
function updateVipHomepageView() {
    const status = Cookies.get('SR_PR_kStatus');
    const isVipCustomer = Cookies.get('customer_vip')?.toLowerCase() === 'vip';
    // const customer_vip = status === 'VIP' ? 'vip' : status === 'Purchaser' ? 'almostvip' : '';
    const VIP_TIERS = ['yotpo-tiers-silver', 'yotpo-tiers-gold'];

    const normalizedTags = tags?.map(tag => tag.toLowerCase()) || [];

    const customer_vip =
      normalizedTags.some(tag => VIP_TIERS.includes(tag))
        ? 'vip'
        : organic_order_spent
        ? 'almostvip'
        : '';

  if (isVipCustomer || customer_vip) {
      document.querySelectorAll('.golf-ab-bn-video.sr-homepage-video , .picked-for-home, sr-tab-contents')
          .forEach(el => el?.classList.remove('hidden'));

      const srTabs = document.querySelector('sr-tabs');
      if (srTabs) srTabs.style.flexDirection = 'row-reverse';

      const tab1Els = document.querySelectorAll('sr-tab[data-tab="sr-tab1"], .sr-tab1');
      const tab2Els = document.querySelectorAll('sr-tab[data-tab="sr-tab2"], .sr-tab2');

      if (tab1Els.length && tab2Els.length) {
          tab1Els.forEach(el => el.classList.remove('active'));
          tab2Els.forEach(el => el.classList.add('active'));
      }

      document.querySelectorAll('.review-section, .section-sr-homepage-colorush, .sr-homepage-banner-mob, .sr-homepage-banner')
          .forEach(el => el?.classList.add('hidden'));
          function updatePadding() {
              const imageTiles = document.querySelector('.section-sr-tab-image-tiles');
              const bestseller = document.querySelector('.hp-men-women-bestseller');
          
              if (imageTiles) {
                  imageTiles.style.paddingBottom = '30px';
              }
          
              if (window.innerWidth > 767) {
                  if (bestseller) {
                      bestseller.style.paddingBottom = '70px';
                  }
                  if (imageTiles) {
                      imageTiles.style.paddingBottom = '50px';
                  }
              }
          }
          
          updatePadding();
          window.addEventListener('resize', updatePadding);
      }
}
// updateVipHomepageView()
//** Retention Customer Homepage ends */
//** Frequently-Bought Together starts */

window.initQuickAddButtons = function () {
  const quickAddButtons = document.querySelectorAll('.quick-add-btn');
  if (!quickAddButtons.length) return;

  quickAddButtons.forEach(btn => {
    // Prevent multiple listeners on the same button
    if (btn.dataset.init) return;
    btn.dataset.init = 'true';

    btn.addEventListener('click', async () => {
      const variantId = btn.dataset.variantId;
      const productHandle = btn.dataset.productHandle;
      if (!variantId) return;

      btn.disabled = true;
      btn.classList.add('loading');

      try {
        const res = await fetch('/cart/add.js', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json'
          },
          body: JSON.stringify({
            id: variantId,
            quantity: 1,
            properties: { _complementary: productHandle }
          })
        });

        const data = await res.json();

        document.dispatchEvent(new CustomEvent('on:cart:change', {
          bubbles: true,
          detail: { data }
        }));

        setTimeout(() => {
          // document.querySelector('.js-cart-drawer')?.open();
          window.location.href = '/cart';
        }, 1000);

        const productCard = btn.closest('.slider__item');
        if (productCard) {
          productCard.classList.add('removed');
          productCard.style.opacity = '0';
          setTimeout(() => {
            productCard.setAttribute('hidden', 'true');
            productCard.style.display = 'none';
            const carousel = btn.closest('carousel-slider');
            if (carousel?.refresh) carousel.refresh();
          }, 300);
        }
        const columnCard = btn.closest('.column');
          if (columnCard) {
          columnCard.classList.add('removed');
          columnCard.style.opacity = '0';
          setTimeout(() => {
              columnCard.setAttribute('hidden', 'true');
              columnCard.style.display = 'none';
          }, 300);
          }
      } catch (err) {
        console.error('Add to cart failed', err);
      } finally {
        btn.disabled = false;
        btn.classList.remove('loading');
      }
    });
  });
};
setTimeout(() => {
  window.initQuickAddButtons();
}, 1500);
document.addEventListener('DOMContentLoaded', window.initQuickAddButtons);
document.addEventListener('shopify:section:load', window.initQuickAddButtons);
document.addEventListener('on:cart:change', async () => {
try {
  const res = await fetch('/cart.js');
  const cart = await res.json();

  const complementaryHandles = cart.items
    .filter(item => item.properties && item.properties._complementary)
    .map(item => item.properties._complementary);

  document.querySelectorAll('carousel-slider[data-complementary]').forEach(carousel => {
    let shouldRefresh = false;

    carousel.querySelectorAll('.slider__item').forEach(item => {
      const handle = item.dataset.productHandle;

      if (!complementaryHandles.includes(handle)) {
        if (item.hasAttribute('hidden')) {
          item.removeAttribute('hidden');
          item.style.display = '';
          item.style.opacity = '1';
          shouldRefresh = true;
        }
      } else {
        if (!item.hasAttribute('hidden')) {
          item.setAttribute('hidden', 'true');
          item.style.display = 'none';
          shouldRefresh = true;
        }
      }
    });

    if (shouldRefresh && carousel.refresh) carousel.refresh();
  });
  document.querySelectorAll('.fixed-layout[data-complementary]').forEach(wrapper => {
    wrapper.querySelectorAll('.column').forEach(item => {
      const handle = item.dataset.productHandle;

      if (!complementaryHandles.includes(handle)) {
        if (item.hasAttribute('hidden')) {
          item.removeAttribute('hidden');
          item.style.display = '';
          item.style.opacity = '1';
        }
      } else {
        if (!item.hasAttribute('hidden')) {
          item.setAttribute('hidden', 'true');
          item.style.display = 'none';
        }
      }
    });
  });
} catch (err) {
  console.error('Error syncing complementary products:', err);
}
});
//** Frequently-Bought Together ends */

/* Save For Later JS Starts */
document.addEventListener('DOMContentLoaded', function () {
window._sflItemCount = 0;  
const tabButtons = document.querySelectorAll('.cart-tab-button');
const tabContents = document.querySelectorAll('.cart-tab-content');
const savedItemsContainer = document.getElementById('saved-for-later-items');
const savedCountEl = document.getElementById('saved-count');
const loader = document.querySelector('.saved-loader');

tabButtons.forEach(btn => {
  btn.addEventListener('click', () => {
    tabButtons.forEach(b => b.classList.remove('active'));
    btn.classList.add('active');

    const target = btn.getAttribute('data-tab');
    tabContents.forEach(tc => tc.classList.remove('active'));
    document.getElementById(target).classList.add('active');
  });
});

const drawer = document.querySelector('.js-cart-drawer');
if (!drawer) return;

drawer.addEventListener('open', () => {
});
});
function initializeSaveForLater(swat) {
let onSuccess = function(response) {
  window._saveforLaterListId = response.list.lid;
};
let onError = function(error) {
  console.log("Error creating Save For Later List", error);
};
 swat.SaveForLater.init(onSuccess, onError);
}

window.SwymCallbacks = window.SwymCallbacks || [];
window.SwymCallbacks.push(initializeSaveForLater);

document.addEventListener('DOMContentLoaded', () => {
const drawer = document.querySelector('.js-cart-drawer');
if (!drawer) return;
drawer.addEventListener('open', () => {
  // waitForSFLReady(initSaveForLater);
});
});

function waitForSFLReady(callback, attempts = 0) {
if (window._swat && window._swat.SaveForLater && window._saveforLaterListId) {
  callback();
} else if (attempts < 20) {
  setTimeout(() => waitForSFLReady(callback, attempts + 1), 300);
} else {
  console.error('Swym or Save For Later list not ready after multiple attempts.');
}
}

function formatVi(raw) {
if (!raw && raw !== 0) return '';
const str = String(raw);
return str
  .replace(/[\\/]/g, '|')
  .split('|')
  .map(s => s.trim())
  .filter(Boolean)
  .map(segment =>
    segment
      .split(/\s+/)
      .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
      .join(' ')
  )
  .join(' | ');
}
async function fetchProductData(items) {
return await Promise.all(items.map(async (listItem) => {
  try {
    const response = await fetch(listItem.du.split('?')[0] + '.js');
    const productData = await response.json();
    listItem.productData = productData;
    return listItem;
  } catch (error) {
    console.error('Failed to fetch product data:', error);
    return listItem;
  }
}));
}
function removeItemFromAllContainers(variantId) {
document.querySelectorAll(`#saved-for-later-items .sfl-item[data-variant="${variantId}"]`).forEach(card => {
  card.style.transition = 'opacity 0.3s';
  card.style.opacity = '0';
  setTimeout(() => card.remove(), 300);
});
}

function bindSFLButtons() {
document.querySelectorAll('.sfl-move').forEach(btn => {
  btn.addEventListener('click', () => {
    moveToCart(btn.dataset.variant, btn.dataset.empi, btn.dataset.du, btn);
  });
});

document.querySelectorAll('.sfl-remove').forEach(btn => {
  btn.addEventListener('click', () => {
    removeFromSFL(btn.dataset.variant, btn.dataset.empi, btn.dataset.du, btn);
  });
});
}

async function moveToCart(variantId, empi, du, btn) {
try {
  btn.disabled = true;
  btn.classList.add('loading');
  const res = await fetch('/cart/add.js', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
    body: JSON.stringify({ id: variantId, quantity: 1 })
  });
  const data = await res.json();
  document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true, detail: { data } }));

  if (typeof attachSaveForLaterButtons === 'function') attachSaveForLaterButtons();

  setTimeout(() => document.querySelector('.js-cart-drawer')?.open(), 800);

  removeItemFromAllContainers(variantId);
  window._sflItemCount = Math.max(0, window._sflItemCount - 1);
  updateSavedCount(window._sflItemCount);

  const productsToRemove = [{ du: du || '', empi: empi || '', epi: variantId }];
  _swat.SaveForLater.remove(window._saveforLaterListId, productsToRemove, () => {
    checkIfSFLIsEmpty();
  });
} catch (err) {
  console.error('moveToCart error:', err);
} finally {
  btn.disabled = false;
  btn.classList.remove('loading');
}
}

function removeFromSFL(variantId, empi, du, btn) {
removeItemFromAllContainers(variantId);
window._sflItemCount = Math.max(0, window._sflItemCount - 1);
updateSavedCount(window._sflItemCount);

const productsToRemove = [{ du: du || '', empi: empi || '', epi: variantId }];
_swat.SaveForLater.remove(window._saveforLaterListId, productsToRemove, () => {
  checkIfSFLIsEmpty();
});
}

function checkIfSFLIsEmpty() {
_swat.SaveForLater.fetch(window._saveforLaterListId, function(response) {
  const containers = document.querySelectorAll('.cart-tab-content.active');
  const isEmpty = !response.items || response.items.length === 0;

  containers.forEach(container => {
    container.style.display = isEmpty ? 'none' : '';
  });

  const emptyMessages = document.querySelectorAll('.swym-storefront-layout-empty-container');
  emptyMessages.forEach(msg => {
    msg.style.display = isEmpty ? '' : 'none';
  });
});
}

function updateSavedCount(count) {
const countEls = document.querySelectorAll('#saved-count');
countEls.forEach(el => {
  el.textContent = count;
});
}
setTimeout(() => {
  
if (document.body.classList.contains('vip') == false) {

  const h = document.querySelector('.home-page');

  if (h) {
    const wrapper = h.closest('.announcement-bar__announcements');

    if (wrapper) {
      const bg = wrapper.parentElement.parentElement.previousElementSibling;

      if (bg && bg.classList.contains('announcement-bg')) {
        bg.style.opacity = "1";
      }
    }
  }

}
}, 700);
/* Save For Later JS Ends */

/* LB Popup */

document.addEventListener("DOMContentLoaded", function(){

  const popupHTML = `
  <div class="sr-info-popup-overlay" id="srInfoPopup">
    <div class="sr-info-popup">
      <span class="sr-popup-close">
        <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M15.6188 13.7255C15.8699 13.9765 16.0109 14.3171 16.0109 14.6721C16.0109 15.0272 15.8699 15.3677 15.6188 15.6188C15.3677 15.8699 15.0272 16.0109 14.6721 16.0109C14.3171 16.0109 13.9765 15.8699 13.7255 15.6188L8.00546 9.88546L2.28546 15.6188C2.03438 15.8699 1.69386 16.0109 1.33879 16.0109C0.98372 16.0109 0.643194 15.8699 0.392122 15.6188C0.141051 15.3677 0 15.0272 0 14.6721C0 14.3171 0.141051 13.9765 0.392122 13.7255L6.12546 8.00546L0.392122 2.28546C0.141051 2.03438 -2.64547e-09 1.69386 0 1.33879C2.64547e-09 0.98372 0.141051 0.643194 0.392122 0.392122C0.643194 0.141051 0.98372 2.64547e-09 1.33879 0C1.69386 -2.64547e-09 2.03438 0.141051 2.28546 0.392122L8.00546 6.12546L13.7255 0.392122C13.9765 0.141051 14.3171 0 14.6721 0C15.0272 0 15.3677 0.141051 15.6188 0.392122C15.8699 0.643194 16.0109 0.98372 16.0109 1.33879C16.0109 1.69386 15.8699 2.03438 15.6188 2.28546L9.88546 8.00546L15.6188 13.7255Z" fill="black"/>
        </svg>
      </span>
      <div class="head">
      <img src="https://cdn.shopify.com/s/files/1/0350/5401/files/Group_116046579.svg?v=1773671634" alt="we have got your back" class="sr-popup-icon" />
      <h3>WE'VE GOT <br> YOUR BACK</h3>
      </div>
      <p>
        If your Shady Rays are ever lost, broken, or stolen, we’ve got you covered up twice with each pair purchased. Just submit a replacement claim and we’ll send you a new pair—no matter what happened. It’s our way of making sure you can wear your shades confidently anywhere.
      </p>
      <a href="/pages/protection-programs" class="btn btn-primary" aria-label="Learn about Protection Programs">
        Learn More
      </a>
    </div>
  </div>
  `;
  
  document.body.insertAdjacentHTML("beforeend", popupHTML);
  
  const popup = document.getElementById("srInfoPopup");
  
  document.querySelectorAll(".sr-learnmore-icon").forEach(icon=>{
    icon.addEventListener("click", function(e){
      e.preventDefault();
      popup.classList.add("active");
    });
  });
  
  document.querySelector(".sr-popup-close").addEventListener("click", function(){
    popup.classList.remove("active");
  });
  
  popup.addEventListener("click", function(e){
    if(e.target === popup){
      popup.classList.remove("active");
    }
  });
  
  });

/* LB Popup End */
/* LB banner */
document.addEventListener('DOMContentLoaded', function () {
  const heroSection = document.querySelector('.section-sr-slideshow');

  if (!heroSection) return;

  let lastScrollY = window.scrollY;

  window.addEventListener('scroll', function () {
    const currentScrollY = window.scrollY;
    if (currentScrollY > 600) {
      heroSection.style.position = 'relative';
    } else {
      heroSection.style.position = 'sticky';
    }

    lastScrollY = currentScrollY;
  });


});
/* LB banner End */
  setTimeout(() => {

const vanityCode = Cookies.get("vu_cc") || "";
const bogowelcome = vanityCode === "BOGOWELCOME" || vanityCode === "DAD40" || vanityCode === "SUMMERBOGO" || vanityCode === "NEWBOGO" || vanityCode === "FALLBOGO";
if (bogowelcome){
  document.querySelectorAll('.BOGO-VANITY-MESSAGE').forEach(el => {
    el.innerHTML = "Discounted prices apply only with 2 regularly priced pairs. Some exclusions apply";
  });
}
  }, 1500);

window.addEventListener('pageshow', (event) => {
  if (event.persisted) {
    window.location.reload();
  }
});
document.addEventListener('DOMContentLoaded', () => {
  if (!document.body.classList.contains('shipping-80')) return;

  document
    .querySelectorAll('.announcement__text .mob-hide, .announcement__text .desk-hide,  .sr-global-note.sr-global-note-wrapper')
    .forEach(el => {
      el.innerHTML = el.innerHTML.replace('$60', '$80');
    });
});

document.querySelectorAll('.template-women .announcement__text').forEach((el) => {
  const mobHide = el.querySelector('.mob-hide');
  const deskHide = el.querySelector('.desk-hide');

  if (mobHide) {
    mobHide.innerHTML = `
      INDEPENDENCE DAY REPLAY • 35% OFF ALL SUNGLASSES • 
      <a href="/collections/women-all-styles" title="Women – All Styles">
        SHOP NOW
      </a>
    `;
  }

  if (deskHide) {
    deskHide.innerHTML = `
      35% OFF ALL SUNGLASSES • 
      <a href="/collections/women-all-styles" title="Women – All Styles">
        SHOP NOW
      </a>
    `;
  }
});
document.addEventListener('DOMContentLoaded', () => {
  setTimeout(() => {
    if(window.Shopify.country == 'CA') {
      document.body.classList.add('sr-store-canada');
    }
  }, 1000);
});


function sortColorSwatches() {
  document.querySelectorAll('.color-swatches').forEach(swatches => {
    const dots = Array.from(swatches.querySelectorAll('.sr-colordots'));

    // Move unavailable dots to the end
    dots.sort((a, b) => {
      const aAvailable = a.dataset.isAvailable === 'true';
      const bAvailable = b.dataset.isAvailable === 'true';

      if (aAvailable === bAvailable) return 0;
      return aAvailable ? -1 : 1;
    });

    // Re-append in sorted order
    dots.forEach(dot => swatches.appendChild(dot));

    // Click the first available dot
    const firstAvailable = swatches.querySelector(
      '.sr-colordots[data-is-available="true"]'
    );

    if (firstAvailable) {
      firstAvailable.click();

      // If your swatches use a hidden radio input:
      const input = firstAvailable.querySelector('input[type="radio"]');
      if (input) {
        input.checked = true;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      }
    }
  });
}

document.addEventListener('DOMContentLoaded', sortColorSwatches);



function limitColorSwatches() {
  const isMobile = window.innerWidth < 901;
  const visibleCount = isMobile ? 6 : 8;

  document.querySelectorAll('.color-swatches').forEach(container => {
    // Remove previous +N if this runs again
    if (document.body.classList.contains('collection-rx')) return;
    container.querySelector('.color-swatches__more')?.remove();

    const swatches = [...container.children].filter(
      el => !el.classList.contains('color-swatches__more')
    );

    swatches.forEach((swatch, index) => {
      swatch.style.display = index < visibleCount ? '' : 'none';
    });

    const hiddenCount = swatches.length - visibleCount;

    if (hiddenCount > 0) {
      const more = document.createElement('a');
      more.className = 'color-swatches__more';
      more.textContent = `+${hiddenCount}`;

      // Get PDP URL from the product card
      const card = container.closest('sr-product-card-women, sr-product-card-new, .product-card');
      const productUrl =
        card?.querySelector('.product-title a')?.href ||
        container.dataset.productUrl;

      if (productUrl) {
        more.href = productUrl;
      }

      container.appendChild(more);
    }
  });
}

document.addEventListener('DOMContentLoaded',  debounce(limitColorSwatches, 200));
window.addEventListener('resize', debounce(limitColorSwatches, 200));

function debounce(fn, wait) {
  let timeout;
  return function () {
    clearTimeout(timeout);
    timeout = setTimeout(fn, wait);
  };
}


function updateCartPrices() {
  if (!isCartPage()) return;

  const externalDiscountApplied = !!document.querySelector('.cart-applied-discount');

  // ✅ Running totals accumulated across all cart line items in the loop
  // below — used to rebuild the subtotal after processing everything.
  let subtotalSkuAdjusted = 0; // "was" total — sum of SKU-adjusted prices
  let subtotalFinal = 0;       // actual/charged total after markdown too
  let subtotalHasMarkdown = false;

  document.querySelectorAll('.cart-item__price-label [data-target="price-element"]').forEach((element) => {
    const productWrapper = element.closest('[data-product-collections]');
    if (!productWrapper) return;

    const parent = element.parentElement;
    const realPrice = productWrapper.dataset.realPrice;
    if (!realPrice) return;

    const originalBasePrice = Number(realPrice) / 100;
    const curr = theme.globals.userCurrency === 'GBP' ? '£' : '$';

    const currentEl = parent.dataset.current !== undefined ? parent : element.closest('[data-current]');
    const sku = currentEl ? currentEl.dataset.sku : productWrapper.dataset.sku;

    const skuAdjustedPrice = (window.SKU_PRICE_ADJUSTMENTS && sku)
      ? MarkdownPricePro.applySkuPriceAdjustment(originalBasePrice, sku)
      : originalBasePrice;

    if (externalDiscountApplied) {
      const wasEl = element.querySelector('.price__was .money') || element.parentElement?.querySelector('.price__was .money');
      if (wasEl) {
        wasEl.textContent = `${curr}${skuAdjustedPrice.toFixed(2)}`;
      }

      // ✅ Still track the "was" total even when an external discount app
      // owns the actual charged number — we only rewrite subtotal's "was" side.
      subtotalSkuAdjusted += skuAdjustedPrice;

      const currentMoneyEl = element.querySelector('.money');
      const currentDisplayed = currentMoneyEl ? parseFloat((currentMoneyEl.textContent || '').replace(/[^0-9.]/g, '')) : skuAdjustedPrice;
      subtotalFinal += Number.isFinite(currentDisplayed) ? currentDisplayed : skuAdjustedPrice;
      if (Math.abs(subtotalFinal - subtotalSkuAdjusted) > 0.001) subtotalHasMarkdown = true;

      return;
    }

    const markdownObject = Cookies.getJSON('markdown_object') || [];
    const activeMarkdowns = markdownObject.filter((obj) => obj.markdown_initialized === 'initialized');

    let finalPrice = skuAdjustedPrice;
    let hasMarkdown = false;

    activeMarkdowns.forEach((object) => {
      const collections = productWrapper.dataset.productCollections
        ? productWrapper.dataset.productCollections.split(' ')
        : [];
      const markdownCollections = object.markdown_collections ? object.markdown_collections.split(',') : [];
      const inCollection = markdownCollections.some((col) => collections.includes(col));
      if (!inCollection) return;

      const multiplier = Number(object.markdown_multiplier);
      const mode = object.markdown_mode;

      let discounted;
      if (mode === 'subtract') {
        discounted = skuAdjustedPrice - (multiplier * theme.globals.currencyConversion[theme.globals.userCurrency]);
      } else {
        discounted = skuAdjustedPrice * multiplier;
      }

      discounted = Math.max(0, Number(discounted.toFixed(2)));

      if (discounted < finalPrice) {
        finalPrice = discounted;
        hasMarkdown = true;
      }
    });

    // ✅ Accumulate into subtotal running totals
    subtotalSkuAdjusted += skuAdjustedPrice;
    subtotalFinal += finalPrice;
    if (hasMarkdown) subtotalHasMarkdown = true;

    let content;
    if (hasMarkdown) {
      content = `<span class="price--on-sale" data-target="price-element" data-value="markdown_price" data-markdown-discounted="true">
        <span class="price-label money markdown-price">${curr}${finalPrice.toFixed(2)}</span>
        <span class="price-label original-price price__was">${curr}${skuAdjustedPrice.toFixed(2)}</span>
      </span>`;
    } else {
      content = `<span data-target="price-element" data-value="markdown_price">
        <span class="price-label money">${curr}${skuAdjustedPrice.toFixed(2)}</span>
      </span>`;
    }

    if (element.classList.contains('original-price')) {
      parent.outerHTML = content;
    } else {
      element.outerHTML = content;
    }

    productWrapper.dataset.discountedPrice = finalPrice;
  });

  // ✅ Rebuild the subtotal from the totals collected above — mirrors the
  // exact same "plain price, no strikethrough" vs "red + strikethrough"
  // rule used for each line item.
  const subtotalEl = document.querySelector('[data-cart-subtotal]');
  if (subtotalEl) {
    const curr = theme.globals.userCurrency === 'GBP' ? '£' : '$';

    if (subtotalHasMarkdown) {
      subtotalEl.innerHTML = `
        <span data-value="markdown_price" class="price--on-sale" data-markdown-discounted="true">
          <span class="price-label money markdown-price">${curr}${subtotalFinal.toFixed(2)}</span>
          <span class="price-label original-price price__was">${curr}${subtotalSkuAdjusted.toFixed(2)}</span>
        </span>`;
      subtotalEl.setAttribute('data-markdown-price', subtotalFinal.toFixed(2));
    } else {
      subtotalEl.innerHTML = `<span class="theme-money" data-cart-subtotal><span class="money">${curr}${subtotalSkuAdjusted.toFixed(2)}</span></span>`;
      subtotalEl.removeAttribute('data-markdown-price');
    }
  }
}

document.addEventListener('DOMContentLoaded', function () {
  if (document.body.classList.contains('price-b')) {
    fetch('/cart.js')
  .then((res) => res.json())
  .then((cart) => {

    async function updateAllEligibleItems(cart) {
        let updatedCart = null;
        const map = window.SKU_PRICE_ADJUSTMENTS || {};

        for (const item of cart.items) {
          const sku = item.sku ? String(item.sku).trim().toUpperCase() : '';
          const subtractAmount = map[sku];
          if (subtractAmount === undefined) continue;

          const expectedDiscount = subtractAmount.toFixed(2);

          const alreadyAdjusted =
            item.properties?.['Adjusted Base Price'] === 'true' &&
            item.properties?.['Discounted Amount'] === expectedDiscount;

          if (alreadyAdjusted) continue;

          try {
            const response = await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({
                id: item.key,
                properties: {
                  ...(item.properties || {}),
                  'Adjusted Base Price': 'true',
                  'Discounted Amount': expectedDiscount
                },
              }),
            });
            updatedCart = await response.json();

            document.dispatchEvent(
              new CustomEvent('on:cart:change', { bubbles: true, detail: { data: updatedCart } })
            );
          } catch (err) {
            console.error('Error updating Adjusted Base Price property', err);
          }
        }
      }
    updateAllEligibleItems(cart);
  })
    setTimeout(updateCartPrices, 700);
  }
});


window.addEventListener('pageshow', function () {
  if (document.body.classList.contains('price-b')) {
    setTimeout(updateCartPrices, 300);
  }
});

document.addEventListener('on:cart:change', function () {
  if (document.body.classList.contains('price-b')) {
    setTimeout(updateCartPrices, 300);
  }
});
document.addEventListener('on:cart:after-merge', function () {
  if (document.body.classList.contains('price-b')) {
   setTimeout(updateCartPrices, 300);
   fetch('/cart.js')
  .then((res) => res.json())
  .then((cart) => {

    async function updateAllEligibleItems(cart) {
        let updatedCart = null;
        const map = window.SKU_PRICE_ADJUSTMENTS || {};

        for (const item of cart.items) {
          const sku = item.sku ? String(item.sku).trim().toUpperCase() : '';
          const subtractAmount = map[sku];
          if (subtractAmount === undefined) continue;

          const expectedDiscount = subtractAmount.toFixed(2);

          const alreadyAdjusted =
            item.properties?.['Adjusted Base Price'] === 'true' &&
            item.properties?.['Discounted Amount'] === expectedDiscount;

          if (alreadyAdjusted) continue;

          try {
            const response = await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
              body: JSON.stringify({
                id: item.key,
                properties: {
                  ...(item.properties || {}),
                  'Adjusted Base Price': 'true',
                  'Discounted Amount': expectedDiscount
                },
              }),
            });
            updatedCart = await response.json();

            document.dispatchEvent(
              new CustomEvent('on:cart:change', { bubbles: true, detail: { data: updatedCart } })
            );
          } catch (err) {
            console.error('Error updating Adjusted Base Price property', err);
          }
        }
      }
    updateAllEligibleItems(cart);
  })
  .catch((err) => console.error('Cart fetch failed', err));
  }
});


function checkSectionHeader() {
    const sectionHeader = document.querySelector(
        '[id$="__header"].section-header'
    );

    if (!sectionHeader) return;

    const rect = sectionHeader.getBoundingClientRect();
    const isAtTop = Math.abs(rect.top) <= 1;

    document.body.classList.toggle('is-at-top', isAtTop);
}

let ticking = false;

function updateSectionHeader() {
    if (ticking) return;

    ticking = true;

    requestAnimationFrame(() => {
        checkSectionHeader();
        ticking = false;
    });
}

window.addEventListener('scroll', updateSectionHeader, {
    passive: true
});

window.addEventListener('resize', updateSectionHeader);

window.addEventListener('load', checkSectionHeader);

checkSectionHeader();

(function () {
  if (window.location.pathname !== '/cart') return;

  let lastShowState = null;

  function shouldShowBogoMessage(cart) {
    const realItems = cart.items.filter(function (item) {
      return item.product_type !== 'Processing';
    });

    return realItems.some(function (item) {
      return item.product_type !== 'Outlet';
    });
  }

  function applyVisibility(show) {
    const bogoMessage = document.querySelector('.BOGO-VANITY-MESSAGE');
    const topDiscountNote = document.getElementById('top-discount-note');
    const vanityUrlMessage = document.getElementById('vanity-url-message');

    if (bogoMessage) bogoMessage.style.display = show ? '' : 'none';
    if (topDiscountNote) topDiscountNote.style.display = show ? '' : 'none';
    if (vanityUrlMessage) vanityUrlMessage.style.display = show ? '' : 'none';
  }

  function updateBogoVisibility() {
    fetch('/cart.js')
      .then(function (res) { return res.json(); })
      .then(function (cart) {
        lastShowState = shouldShowBogoMessage(cart);
        applyVisibility(lastShowState);
      })
      .catch(function (err) {
        console.error('BOGO message visibility check failed:', err);
      });
  }

  let debounceTimer;
  function scheduleUpdate() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(updateBogoVisibility, 150);
  }

  document.addEventListener('DOMContentLoaded', updateBogoVisibility);

  document.addEventListener('cart:updated', scheduleUpdate);
  document.addEventListener('cart:refresh', scheduleUpdate);

  const cartForm = document.querySelector('cart-form');
  if (cartForm) {
    const observer = new MutationObserver(scheduleUpdate);
    observer.observe(cartForm, { childList: true, subtree: true });
  }

  const lateInsertObserver = new MutationObserver(function (mutations) {
    for (const mutation of mutations) {
      for (const node of mutation.addedNodes) {
        if (node.nodeType !== 1) continue;
        const isVanityMessage =
          node.id === 'vanity-url-message' ||
          (node.querySelector && node.querySelector('#vanity-url-message'));
        if (isVanityMessage && lastShowState !== null) {
          applyVisibility(lastShowState);
          return;
        }
      }
    }
  });
  lateInsertObserver.observe(document.body, { childList: true, subtree: true });
})();
// document.addEventListener('DOMContentLoaded', function () {
//   if (document.body.getAttribute('data-country') === 'US') {
//     const announcementText = 'S|R X PAPPY & CO. | PAIR & POUR SWEEPSTAKES - <a href="/sweepstakes">LEARN MORE</a>';

//     document.querySelectorAll('.announcement__text .mob-hide, .announcement__text .desk-hide')
//       .forEach(function (el) {
//         el.innerHTML = announcementText;
//       });
//   }
// });